import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom'; // v642.13: portal floating online panel to body
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useGame } from '../context/GameContext';
import { useAuth } from '../context/AuthContext';
import useKeybinds from '../hooks/useKeybinds';
import useAFKDetection from '../hooks/useAFKDetection';
import MapGrid from './MapGrid';
import CharacterStats from './CharacterStats';
import ActionsPanel from './ActionsPanel';
import ChatPanel from './ChatPanel';
import CharacterSheet from './CharacterSheet';
import GameLog from './GameLog';
import MusicPlayer from './MusicPlayer';
import CombatInterface from './CombatInterface';
import PostBattleScreen from './PostBattleScreen';
import ShopModal from './ShopModal';
import MarketModal from './MarketModal';
import ArenaModal from './ArenaModal';
import InventoryPanel from './InventoryPanel';
import MessagesModal from './MessagesModal';
import SkillTreePanel from './SkillTreePanel';
import CraftingPanel from './CraftingPanel';
import StashPanel from './StashPanel';
import OnlinePlayersPanel from './OnlinePlayersPanel';
import LevelUpModal from './LevelUpModal';
import AdvancedClassSelection from './AdvancedClassSelection';
import BugReportModal from './BugReportModal';
import ReportPlayerModal from './ReportPlayerModal';
import Compendium from './Compendium';
import InventoryOverflowModal from './InventoryOverflowModal';
import GuildPanel from './GuildPanel';
import InspectModal from './InspectModal';
import FellowshipPanel from './FellowshipPanel';
import FellowshipCombatInterface from './FellowshipCombatInterface';
import BountyBoardPanel from './BountyBoardPanel';
import ActiveBountiesPanel from './ActiveBountiesPanel';
import DailyDashboard from './DailyDashboard';
import AchievementsModal from './AchievementsModal';
import NotificationCenter from './NotificationCenter';
import DeckManager from './DeckManager';
import SpellbookPanel from './SpellbookPanel';
import CodeOfConductModal from './CodeOfConductModal';
import CharacterHubTabBar from './CharacterHubTabBar'; // v642.1
import AdventureHubTabBar from './AdventureHubTabBar'; // v642.2
import SocialDropdown from './SocialDropdown'; // v642.4
import MobileChatStrip from './MobileChatStrip'; // v642.7
import QuickSettingsPopover from './QuickSettingsPopover'; // v642.8
import { CharacterProvider } from '../contexts/CharacterContext'; // v642.5
import DelveModal from './DelveModal';
import OverworldEventModal from './OverworldEventModal';
import WelcomeModal from './WelcomeModal';
import PatchNotesModal, { getLastSeenVersion } from './PatchNotesModal';
import { LATEST_VERSION } from '../data/patchNotes';
import AFKModal from './AFKModal';
import { formatCurrency } from './shared/formatCompact';
import CrownShopModal from './CrownShopModal';import WorldTierModal from './WorldTierModal';
import InfiniteQuestPanel from './InfiniteQuestPanel';
import AtlasPanel from './AtlasPanel';
import ProgressionGuidePanel from './ProgressionGuidePanel';
import HintBanner from './HintBanner';
import QuestTracker from './QuestTracker';
import SettingsPanel from './SettingsPanel';
import useIsMobile from '../hooks/useIsMobile';
import '../styles/Admin.css';
import '../styles/MobileGame.css';
import '../styles/DesktopModern.css';

// Character portrait overrides (name → image path) — legacy fallback
// for characters created before v679's per-character portrait selector.
const CHARACTER_PORTRAITS = {
  'James': '/images/character_portraits/james_character.png',
  'Tres El Fahad': '/images/character_portraits/tres_character.png',
};

// v679: Resolve the active player's portrait. Prefers the per-character
// portrait_url chosen via the picker, then the legacy name map, then null.
function resolveCharacterPortrait(character) {
  if (!character) return null;
  if (character.portrait_url) return character.portrait_url;
  if (CHARACTER_PORTRAITS[character.name]) return CHARACTER_PORTRAITS[character.name];
  return null;
}

// XP formula matching server
function characterLevel(xp) {
  return Math.floor((Math.sqrt(1 + 8 * Math.floor(xp / 1000)) - 1) / 2) + 1;
}

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

const GamePage = () => {
  const { gameState, setGameState, isConnected, movePlayer, performAction, sendChatMessage, refreshCharacterData, requestGameState, requestOnlinePlayers, socket, chatType, setChatType, dismissFellowshipInvite } = useGame();
  const fellowshipBattle = gameState.fellowshipBattle; // extracted for stable hook deps
  const { user, logout, switchCharacter } = useAuth();
  const navigate = useNavigate();
  // v640.5: User-rebindable letter keys (movement & arrows still hardcoded)
  const { binds: keybinds } = useKeybinds();
  const [buildVersion, setBuildVersion] = useState('loading');
  const [showCharacterSheet, setShowCharacterSheet] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentBattle, setCurrentBattle] = useState(null);
  const [spottedMonster, setSpottedMonster] = useState(null);
  const [showShop, setShowShop] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [isOnShopTile, setIsOnShopTile] = useState(false);
  const [showMarket, setShowMarket] = useState(false);
  const [isOnMarketTile, setIsOnMarketTile] = useState(false);
  const [showArena, setShowArena] = useState(false);
  const [isOnArenaTile, setIsOnArenaTile] = useState(false);
  const [inArenaBattle, setInArenaBattle] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showSkillTree, setShowSkillTree] = useState(false);
  const [showCrafting, setShowCrafting] = useState(false);
  const [isOnCraftTile, setIsOnCraftTile] = useState(false);
  const [showStash, setShowStash] = useState(false);
  const [isOnStashTile, setIsOnStashTile] = useState(false);
  const [gatherNode, setGatherNode] = useState(null); // { type: "mining"|"herb", tier: "low"|"mid"|"high" }
  const [gatherResult, setGatherResult] = useState(null);
  const [isGathering, setIsGathering] = useState(false);
  const [gatherCooldown, setGatherCooldown] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [mailPulse, setMailPulse] = useState(false); // One-time pulse on login if unread mail
  const [crownPulse, setCrownPulse] = useState(false); // One-time gold pulse on milestone crown grant
  const [battleMusic, setBattleMusic] = useState(null);
  const [battleResult, setBattleResult] = useState(null);
  const [mobileTab, setMobileTab] = useState('map');
  const [whisperTarget, setWhisperTarget] = useState(null);
  const [reportTarget, setReportTarget] = useState(null); // { target, recentMessages }
  const [mailRecipient, setMailRecipient] = useState(null);
  const [pendingLevelUp, setPendingLevelUp] = useState(false);
  const [pendingACSelection, setPendingACSelection] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [showCompendium, setShowCompendium] = useState(false);
  const [generatingWorldMap, setGeneratingWorldMap] = useState(false);
  const [adminBroadcast, setAdminBroadcast] = useState(null); // { message, sentBy, sentAt }
  const [broadcastDismissed, setBroadcastDismissed] = useState(false);
  const [inventoryOverflow, setInventoryOverflow] = useState(null); // { inventory, maxCapacity }
  const [showGuild, setShowGuild] = useState(false);
  const [showFellowship, setShowFellowship] = useState(false);
  const [showBountyBoard, setShowBountyBoard] = useState(false);
  const [showActiveBounties, setShowActiveBounties] = useState(false);
  const [showDailyDashboard, setShowDailyDashboard] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [isOnBountyTile, setIsOnBountyTile] = useState(false);
  const [showDeckHub, setShowDeckHub]       = useState(false);
  const [showSpellbook, setShowSpellbook]   = useState(false);
  const [showCoC, setShowCoC]               = useState(false);
  const [deckHubInitTab, setDeckHubInitTab]  = useState('library');
  const [showDelve, setShowDelve]            = useState(false);
  const [isOnDelveTile, setIsOnDelveTile]    = useState(false);
  const [isNearCampfire, setIsNearCampfire]   = useState(false);
  const [campfireRestResult, setCampfireRestResult] = useState(false); // true after campfire rest completes
  const [bountyHudInfo, setBountyHudInfo] = useState(null); // { active, claimable, total }
  const [inspectTargetId, setInspectTargetId] = useState(null);
  const isMobile = useIsMobile(768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [overworldEvent, setOverworldEvent] = useState(null);
  const [overworldEventResult, setOverworldEventResult] = useState(null);
  const [overworldNextStep, setOverworldNextStep] = useState(null);
  const [warpChoice, setWarpChoice] = useState(null); // { spellName, warpType, ... }
  const [waypointName, setWaypointName] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [showPatchNotes, setShowPatchNotes] = useState(false);
  const [patchNotesSinceVersion, setPatchNotesSinceVersion] = useState(null);
  // v641.5: AFK auto-logout. Disabled while in active combat so a long
  // animation pause doesn't kick the player out mid-battle.
  const { isAFK, secondsLeft: afkSecondsLeft, dismiss: dismissAFK } = useAFKDetection({
    idleMs: 5 * 60 * 1000,
    warningMs: 60 * 1000,
    enabled: !currentBattle,
    onTimeout: () => { try { logout(); } catch (e) {} navigate('/login'); },
  });
  const [showCrownShop, setShowCrownShop] = useState(false);
  const [showInfiniteQuest, setShowInfiniteQuest] = useState(false);
  const [showWorldTier, setShowWorldTier] = useState(false);
  const [showAtlas, setShowAtlas] = useState(false);
  const [showProgressionGuide, setShowProgressionGuide] = useState(false);
  const [guideSection, setGuideSection] = useState('overview');
  // Modern desktop layout state
  const [desktopLayout, setDesktopLayout] = useState(() => localStorage.getItem('loria_desktop_layout') || 'modern');
  const [showSwitchPopup, setShowSwitchPopup] = useState(false);
  const [showDesktopMenu, setShowDesktopMenu] = useState(false);
  const [showDesktopChat, setShowDesktopChat] = useState(true);
  const [showDesktopLog, setShowDesktopLog] = useState(true);
  const [showDesktopOnline, setShowDesktopOnline] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // v642.7 / v642.8
  const [showMobileOnline, setShowMobileOnline] = useState(false);
  const [showQuickSettings, setShowQuickSettings] = useState(false);
  const quickSettingsAnchorRef = useRef(null); // v642.11: anchor for portalled popover
  const [dmViewCols, setDmViewCols] = useState(() => Math.max(5, Math.ceil(window.innerWidth / 100) | 1));
  const [dmViewRows, setDmViewRows] = useState(() => Math.max(5, Math.ceil(window.innerHeight / 100) | 1));
  const switchPopupRef = useRef(null);
  const recentActionsRef = useRef([]);  // Track last 30 player actions for bug reports
  const bountyHintShownRef = useRef(false); // Only show bounty login hint once per session
  const dailyLoginShownRef = useRef(false); // Only show daily login toast once per session
  const welcomeBackShownRef = useRef(false); // Only show welcome-back log line once per session
  const gameLogRef = useRef();
  const lastNpcTileRef = useRef(null); // Track tile of last completed conversation to prevent re-trigger
  const convoBattleCallbacksRef = useRef(null); // Store won/lost/ran nodes for conversation-triggered battles
  const pendingBattleEndRef = useRef(null); // Store battle_ended data while combat UI shows final blow (Bug #250)
  const showDelveRef = useRef(false);          // Mirror of showDelve state for use in socket handlers (avoids stale closure)
  const combatErrorRef = useRef({});           // Holds a resetLock() callback injected by CombatInterface on every render
  const gameStateRef = useRef(gameState); // Stable ref for error reporter (avoids re-registering listeners)
  const currentBattleRef = useRef(null); // Stable ref for error reporter (battle state snapshot)
  const lastErrorReportRef = useRef(0); // Throttle auto-error reports
  const lastBattleUpdateAtRef = useRef(Date.now());

  // ── Per-character welcome modal ──
  useEffect(() => {
    if (gameState.character?.id) {
      const seen = localStorage.getItem(`loria_welcome_seen_${gameState.character.id}`);
      if (!seen) setShowWelcome(true);
    }
  }, [gameState.character?.id]);

  // ── v641.2: Patch notes on first login of a new version ──
  useEffect(() => {
    if (!gameState.character?.id) return;
    const lastSeen = getLastSeenVersion();
    if (lastSeen !== LATEST_VERSION) {
      setPatchNotesSinceVersion(lastSeen);
      // Defer slightly so welcome modal (if any) takes priority
      const t = setTimeout(() => setShowPatchNotes(true), 600);
      return () => clearTimeout(t);
    }
  }, [gameState.character?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadVersion = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/version`);
        if (!response.ok) throw new Error(`Version request failed: ${response.status}`);
        const data = await response.json();
        if (!cancelled) setBuildVersion(data?.version || 'unknown');
      } catch (error) {
        if (!cancelled) setBuildVersion('unknown');
      }
    };

    loadVersion();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Dynamically size desktop modern map to fill viewport ----
  // Use the maze's actual dimensions so tiles scale up to fill the screen
  useEffect(() => {
    if (desktopLayout !== 'modern') return;
    const maze = gameState?.maze;
    if (maze) {
      const mazeCols = maze.cols || 10;
      const mazeRows = maze.rows || 10;
      setDmViewCols(mazeCols);
      setDmViewRows(mazeRows);
    }
  }, [desktopLayout, gameState?.maze]);

  // ---- Dismiss switch popup on click outside ----
  useEffect(() => {
    if (!showSwitchPopup) return;
    const handler = (e) => {
      if (switchPopupRef.current && !switchPopupRef.current.contains(e.target)) {
        setShowSwitchPopup(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSwitchPopup]);

  // ---- Lock screen orientation to landscape on mobile ----
  useEffect(() => {
    if (!isMobile) return;
    const tryLock = async () => {
      try {
        const orientation = window.screen?.orientation;
        if (orientation?.lock) {
          await orientation.lock('landscape');
        }
      } catch (_) {
        // Orientation lock requires fullscreen on most browsers.
        // CSS rotation in MobileGame.css handles the fallback.
      }
    };
    tryLock();
    return () => {
      try { window.screen?.orientation?.unlock?.(); } catch (_) {}
    };
  }, [isMobile]);

  // ---- Action tracking for bug reports ----
  // Keep gameState ref in sync (avoids adding gameState as a dep to the error reporter effect)
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);
  useEffect(() => { currentBattleRef.current = currentBattle; }, [currentBattle]);

  // ---- Daily login reward toast ----
  // Skipped if welcomeBack payload is present (welcome-back log line covers it).
  useEffect(() => {
    if (!gameState.dailyLoginReward || dailyLoginShownRef.current) return;
    if (gameState.welcomeBack) { dailyLoginShownRef.current = true; return; }
    dailyLoginShownRef.current = true;
    const r = gameState.dailyLoginReward;
    setTimeout(() => {
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: `🎁 Daily Login Bonus (Day ${r.streak})! +${r.gold} gold, +${r.xp} XP`
      });
    }, 1500);
  }, [gameState.dailyLoginReward, gameState.welcomeBack]);

  // ---- Ambient "welcome back" log line for returning players ----
  // Shows ONE tasteful line in the game log on session start, summarising
  // streak / rested XP / unread mail. No modal, no banner.
  useEffect(() => {
    if (!gameState.welcomeBack || welcomeBackShownRef.current) return;
    const charName = gameState.character?.name;
    if (!charName) return;
    welcomeBackShownRef.current = true;
    const w = gameState.welcomeBack;
    const r = gameState.dailyLoginReward; // may also be present today
    const parts = [];
    if (r) {
      parts.push(`+${r.gold}g, +${r.xp} XP claimed`);
      if (r.streak > 1) parts.push(`🔥 ${r.streak}-day streak`);
    } else if (w.streak > 1) {
      parts.push(`🔥 ${w.streak}-day streak`);
    }
    if (w.restedXpPct > 0) parts.push(`😴 ${w.restedXpPct}% rested XP`);
    if (w.unreadMail > 0) parts.push(`📬 ${w.unreadMail} unread`);
    const suffix = parts.length ? ` (${parts.join(' · ')})` : '';
    // v641.6: Confirm position resume in the welcome-back line so the player
    // knows their location was preserved.
    const locLine = (w.area && w.x !== undefined && w.x !== null)
      ? `📍 Resumed in ${w.area} at (${w.x}, ${w.y}).`
      : null;
    setTimeout(() => {
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: `Welcome back, ${charName}. The realm has missed you.${suffix}`
      });
      if (locLine) {
        gameLogRef.current?.addLogEntry({ type: 'system', text: locLine });
      }
    }, 800);
    // v641.6: Toast when a stale combat session was recovered (server restart
    // happened while the player was mid-fight). The grace-window resume is
    // silent; this only fires for the unrecoverable case.
    if (w.staleBattleCleared) {
      setTimeout(() => {
        try { toast('⚔️ Your previous battle ended — you woke up safely back in the realm.', { duration: 5500 }); } catch (e) {}
      }, 1200);
    }
    // One-time mail badge pulse if player has unread mail on login
    if (w.unreadMail > 0) {
      setMailPulse(true);
      setTimeout(() => setMailPulse(false), 2400);
    }
  }, [gameState.welcomeBack, gameState.dailyLoginReward, gameState.character?.name]);

  useEffect(() => {
    if (!socket) return;

    const maybeResyncBattle = () => {
      const battle = currentBattleRef.current;
      if (!battle || battle.status !== 'active') return;
      if (Date.now() - lastBattleUpdateAtRef.current < 6000) return;
      socket.emit('request_battle_resync');
      lastBattleUpdateAtRef.current = Date.now();
    };

    const intervalId = setInterval(maybeResyncBattle, 2000);
    window.addEventListener('focus', maybeResyncBattle);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', maybeResyncBattle);
    };
  }, [socket]);

  const trackAction = useCallback((type, detail) => {
    recentActionsRef.current = [
      ...recentActionsRef.current.slice(-49),
      { type, detail, timestamp: Date.now() }
    ];
  }, []);

  // ---- Passive error reporter (auto-submit on uncaught errors) ----
  useEffect(() => {
    const submitErrorReport = async (errorData) => {
      // Throttle: max 1 auto-report per 30 seconds
      const now = Date.now();
      if (now - lastErrorReportRef.current < 30000) return;
      lastErrorReportRef.current = now;

      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const gs = gameStateRef.current; // use ref, not state
        const stateSnapshot = {
          character: gs.character ? {
            name: gs.character.name,
            class: gs.character.class,
            level: gs.character.level,
            max_hp: gs.character.max_hp,
            damage: gs.character.damage,
            current_area: gs.character.current_area,
            x_position: gs.character.x_position,
            y_position: gs.character.y_position,
            battle_state: gs.character.battle_state,
            equipment: gs.character.equipment,
          } : null,
          area: gs.area,
          isConnected: gs.isConnected,
        };
        // Snapshot current combat state if in a battle
        const battle = currentBattleRef.current;
        const combatSnapshot = battle ? {
          status: battle.status, round: battle.round, turn: battle.turn,
          monster: battle.monster ? { name: battle.monster.name, hp: battle.monster.currentHp + '/' + battle.monster.hp } : null,
          char: battle.character ? { hp: battle.character.currentHp + '/' + battle.character.hp, mp: battle.character.currentMp } : null,
          deck: battle.deck ? { ap: battle.deck.ap + '/' + battle.deck.maxAp, hand: battle.deck.hand?.length || 0 } : null,
          logTail: (battle.log || []).slice(-5),
        } : null;
        await fetch(`${API_BASE_URL}/api/bugs/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            reportType: 'auto',
            description: '',
            gameState: stateSnapshot,
            combatState: combatSnapshot,
            recentActions: recentActionsRef.current,
            errorData,
            clientUrl: window.location.href,
          }),
        });
      } catch (_) { /* don't throw from error handler */ }
    };

    const handleGlobalError = (event) => {
      submitErrorReport({
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
      });
    };

    const handleUnhandledRejection = (event) => {
      submitErrorReport({
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        source: 'unhandledrejection',
      });
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []); // stable: reads gameState from ref

  const openProgressionGuide = useCallback((section = 'overview') => {
    setGuideSection(section);
    setShowProgressionGuide(true);
  }, []);

  // v642.1: Character Hub tab switching. The four character-side panels
  // (Equipment, Inventory, Skills, Deck) share a floating tab strip so a
  // player gearing up doesn't have to close one modal to open another.
  // We don't add a new piece of state — the active tab is derived from
  // whichever of the existing show* booleans is true.
  const characterHubTab = showCharacterSheet ? 'equipment'
    : showInventory ? 'inventory'
    : showSkillTree ? 'skills'
    : showDeckHub ? 'deck'
    : null;
  const switchCharacterHubTab = useCallback((tab) => {
    setShowCharacterSheet(tab === 'equipment');
    setShowInventory(tab === 'inventory');
    setShowSkillTree(tab === 'skills');
    if (tab === 'deck') {
      setDeckHubInitTab('library');
      setShowDeckHub(true);
    } else {
      setShowDeckHub(false);
    }
  }, []);

  // v642.2: Adventure Hub tab switching. Same pattern as Character Hub —
  // floating tab strip lets player jump between Quests / Atlas / Bounties /
  // Daily / Achievements / Guide without nesting modals.
  const adventureHubTab = showCompendium ? 'quests'
    : showAtlas ? 'atlas'
    : showActiveBounties ? 'bounties'
    : showDailyDashboard ? 'daily'
    : showAchievements ? 'achievements'
    : showProgressionGuide ? 'guide'
    : null;
  const switchAdventureHubTab = useCallback((tab) => {
    setShowCompendium(tab === 'quests');
    setShowAtlas(tab === 'atlas');
    setShowActiveBounties(tab === 'bounties');
    setShowDailyDashboard(tab === 'daily');
    setShowAchievements(tab === 'achievements');
    if (tab === 'guide') {
      setGuideSection('overview');
      setShowProgressionGuide(true);
    } else {
      setShowProgressionGuide(false);
    }
  }, []);

  // ---- Body scroll locking for modals (prevents iOS Safari background scroll) ----
  const anyModalOpen = showShop || showInventory || showMarket || showArena || showMessages
    || showSkillTree || showCrafting || showStash || showCharacterSheet || showBugReport
    || showCompendium || showBountyBoard || showActiveBounties || showDeckHub
    || showGuild || showFellowship || showDelve || showAtlas || showProgressionGuide || !!currentConversation
    || !!currentBattle || !!battleResult || !!inventoryOverflow;

  useEffect(() => {
    if (anyModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [anyModalOpen]);

  // Keyboard controls
  const handleKeyPress = useCallback((event) => {
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return; // Don't handle keyboard if user is typing
    }

    // Close crafting with Escape
    if (showCrafting && event.key === 'Escape') {
      setShowCrafting(false);
      return;
    }

    // Close stash with Escape
    if (showStash && event.key === 'Escape') {
      setShowStash(false);
      return;
    }

    // Close compendium with Escape
    if (showCompendium && event.key === 'Escape') {
      setShowCompendium(false);
      return;
    }

    // Close bounty board with Escape
    if (showBountyBoard && event.key === 'Escape') {
      setShowBountyBoard(false);
      return;
    }

    // Close active bounties panel with Escape
    if (showActiveBounties && event.key === 'Escape') {
      setShowActiveBounties(false);
      return;
    }

    // Close bug report with Escape
    if (showBugReport && event.key === 'Escape') {
      setShowBugReport(false);
      return;
    }

    // Close deck hub with Escape
    if (showDeckHub && event.key === 'Escape') {
      setShowDeckHub(false);
      return;
    }

    if (showSkillTree && event.key === 'Escape') {
      setShowSkillTree(false);
      return;
    }

    // Close messages modal with Escape
    if (showMessages && event.key === 'Escape') {
      setShowMessages(false);
      return;
    }

    // Close character sheet with Escape
    if (showCharacterSheet && event.key === 'Escape') {
      setShowCharacterSheet(false);
      return;
    }

    // Close conversation with Escape
    if (currentConversation && event.key === 'Escape') {
      closeConversation();
      return;
    }

    // Close shop with Escape
    if (showShop && event.key === 'Escape') {
      setShowShop(false);
      return;
    }

    // Close market with Escape
    if (showMarket && event.key === 'Escape') {
      setShowMarket(false);
      return;
    }

    // Close arena with Escape (only if not in a PvP battle)
    if (showArena && !inArenaBattle && event.key === 'Escape') {
      setShowArena(false);
      return;
    }

    // Close inventory with Escape or I
    if (showInventory && (event.key === 'Escape' || event.key.toLowerCase() === keybinds.inventory)) {
      setShowInventory(false);
      return;
    }

    // Close delve modal with Escape
    if (showDelve && event.key === 'Escape') {
      setShowDelve(false);
      return;
    }

    // Close atlas with Escape
    if (showAtlas && event.key === 'Escape') {
      setShowAtlas(false);
      return;
    }

    // Close progression guide with Escape
    if (showProgressionGuide && event.key === 'Escape') {
      setShowProgressionGuide(false);
      return;
    }

    // Don't handle other keys if character sheet is open or combat modal is showing
    if (showCharacterSheet || showShop || showMarket || showArena || showInventory || showMessages || showSkillTree || showCrafting || showStash || showBugReport || showCompendium || showBountyBoard || showActiveBounties || showDeckHub || showSpellbook || showCoC || showDelve || showAtlas || showProgressionGuide || currentBattle || fellowshipBattle || overworldEvent) return;

    const key = event.key.toLowerCase();
    // Movement (WASD + arrows) is hardcoded — never rebindable
    switch (key) {
      case 'w':
      case 'arrowup':
        if (currentConversation) { setCurrentConversation(null); }
        gameLogRef.current?.clearDialogue?.();
        setSpottedMonster(null);
        movePlayer('N');
        return;
      case 's':
      case 'arrowdown':
        if (currentConversation) { setCurrentConversation(null); }
        gameLogRef.current?.clearDialogue?.();
        setSpottedMonster(null);
        movePlayer('S');
        return;
      case 'a':
      case 'arrowleft':
        if (currentConversation) { setCurrentConversation(null); }
        gameLogRef.current?.clearDialogue?.();
        setSpottedMonster(null);
        movePlayer('W');
        return;
      case 'd':
      case 'arrowright':
        if (currentConversation) { setCurrentConversation(null); }
        gameLogRef.current?.clearDialogue?.();
        setSpottedMonster(null);
        movePlayer('E');
        return;
      case ' ':
        event.preventDefault();
        return;
      default:
        break;
    }
    // v640.5: rebindable action keys (only fire if no movement matched above)
    if (key === keybinds.charSheet) {
      event.preventDefault();
      setShowCharacterSheet(true);
    } else if (key === keybinds.inventory) {
      event.preventDefault();
      setShowInventory(true);
    } else if (key === keybinds.shop) {
      event.preventDefault();
      if (isOnShopTile) setShowShop(true);
    } else if (key === keybinds.market) {
      event.preventDefault();
      if (isOnMarketTile) setShowMarket(true);
    } else if (key === keybinds.arena) {
      event.preventDefault();
      if (isOnArenaTile) setShowArena(true);
    } else if (key === keybinds.skillTree || key === 'k') {
      event.preventDefault();
      setShowSkillTree(true);
    } else if (key === keybinds.compendium) {
      event.preventDefault();
      setShowCompendium(true);
    } else if (key === keybinds.guide) {
      event.preventDefault();
      openProgressionGuide('overview');
    }
  }, [movePlayer, showCharacterSheet, showShop, showMarket, showArena, showInventory, showMessages, showSkillTree, showCrafting, showBugReport, showCompendium, showStash, showBountyBoard, showActiveBounties, showDeckHub, showSpellbook, showCoC, showDelve, showAtlas, showProgressionGuide, isOnShopTile, isOnMarketTile, isOnArenaTile, isOnDelveTile, currentConversation, currentBattle, inArenaBattle, fellowshipBattle, overworldEvent, openProgressionGuide, keybinds]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // ---- Track key game events for bug reports ----
  useEffect(() => {
    if (!socket) return;
    const makeTracker = (type) => (data) => {
      const detail = typeof data === 'object'
        ? (data.message || data.monsterName || data.npcName || data.result || data.area || JSON.stringify(data).substring(0, 200))
        : String(data);
      trackAction(type, detail);
    };
    const trackMove = () => {
      const c = gameStateRef.current.character;
      if (c) trackAction('move', `${c.current_area} (${c.x_position},${c.y_position})`);
    };

    // Capture stable references so socket.off() removes the exact same functions
    const trackMonster = makeTracker('monster_spotted');
    const trackCombat = makeTracker('combat_started');
    const trackBattleEnd = makeTracker('battle_ended');
    const trackAction2 = makeTracker('action_result');
    const trackProcess = makeTracker('area_process');

    socket.on('game_state', trackMove);
    socket.on('monster_spotted', trackMonster);
    socket.on('combat_started', trackCombat);
    socket.on('battle_ended', trackBattleEnd);
    socket.on('action_result', trackAction2);
    socket.on('area_process', trackProcess);
    return () => {
      socket.off('game_state', trackMove);
      socket.off('monster_spotted', trackMonster);
      socket.off('combat_started', trackCombat);
      socket.off('battle_ended', trackBattleEnd);
      socket.off('action_result', trackAction2);
      socket.off('area_process', trackProcess);
    };
  }, [socket, trackAction]);

  // Fetch unread message count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/api/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadMessageCount(data.count || 0);
      }
    } catch (error) {
      // Silently fail - non-critical
    }
  }, []);

  // Fetch unread count once on mount — real-time updates come via socket 'new_message' event
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Listen for real-time new message notifications via socket
  useEffect(() => {
    if (!socket) return;
    const handleNewMessage = (data) => {
      setUnreadMessageCount(prev => prev + 1);
      // Add a log entry so the player knows they got a message
      if (gameLogRef.current && data?.senderName) {
        gameLogRef.current.addLogEntry({
          type: 'system',
          text: `📨 New message from <b>${data.senderName}</b>: ${data.subject || 'No Subject'}`
        });
      }
    };
    socket.on('new_message', handleNewMessage);
    return () => socket.off('new_message', handleNewMessage);
  }, [socket]);

  // Listen for free-crown milestone grants — ambient game-log line + brief HUD pulse.
  useEffect(() => {
    if (!socket) return;
    const handleCrownMilestone = (data) => {
      if (!data || !data.amount) return;
      const reason = data.reason || 'a notable triumph';
      if (gameLogRef.current) {
        gameLogRef.current.addLogEntry({
          type: 'system',
          text: `${reason}, the realm awards you 👑 ${data.amount} Crowns. Spend them in the Crown Shop.`
        });
      }
      // Trigger a brief gold pulse on the crown counter
      setCrownPulse(true);
      setTimeout(() => setCrownPulse(false), 2400);
      // Refresh character data so the new crown balance shows up immediately
      try { refreshCharacterData(); } catch (_) { /* non-fatal */ }
    };
    socket.on('crown_milestone', handleCrownMilestone);
    return () => socket.off('crown_milestone', handleCrownMilestone);
  }, [socket]);

  // Listen for admin broadcast events via socket
  useEffect(() => {
    if (!socket) return;
    const handleBroadcast = (data) => {
      setAdminBroadcast(data);
      setBroadcastDismissed(false); // New broadcast resets dismissal
    };
    const handleBroadcastClear = () => {
      setAdminBroadcast(null);
      setBroadcastDismissed(false);
    };
    socket.on('admin_broadcast', handleBroadcast);
    socket.on('admin_broadcast_clear', handleBroadcastClear);
    return () => {
      socket.off('admin_broadcast', handleBroadcast);
      socket.off('admin_broadcast_clear', handleBroadcastClear);
    };
  }, [socket]);

  // v249: Fellowship bounty progress notifications
  useEffect(() => {
    if (!socket) return;
    const handleBountyProgress = (data) => {
      if (data.progress && data.progress.length > 0) {
        for (const bp of data.progress) {
          const color = bp.complete ? '#2ecc71' : '#f0c040';
          const msg = bp.complete
            ? `📜 Bounty complete! <strong>${bp.monster}</strong> — return to a Tavern to claim your reward!`
            : `📜 Bounty: <strong>${bp.monster}</strong> ${bp.current}/${bp.target}`;
          gameLogRef.current?.addLogEntry({
            type: 'system',
            text: `<span style="color:${color}">${msg}</span>`
          });
        }
      }
    };
    socket.on('bounty_progress', handleBountyProgress);
    return () => { socket.off('bounty_progress', handleBountyProgress); };
  }, [socket]);

  // v249c: Fetch bounty status for HUD indicator + login hint (lightweight endpoint)
  const fetchBountyStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/api/bounty/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const { active, claimable, total } = await res.json();
      setBountyHudInfo({ active, claimable, total });

      // Login hint: show once per session when bounties are available
      if (!bountyHintShownRef.current && gameLogRef.current) {
        bountyHintShownRef.current = true;
        if (claimable > 0) {
          gameLogRef.current.addLogEntry({
            type: 'system',
            text: `<span style="color:#2ecc71">📜 You have ${claimable} completed bount${claimable === 1 ? 'y' : 'ies'} to claim! Visit any <strong>Tavern</strong> to collect your rewards.</span>`
          });
        } else if (active > 0) {
          gameLogRef.current.addLogEntry({
            type: 'system',
            text: `<span style="color:#f0c040">📜 You have ${active} active bount${active === 1 ? 'y' : 'ies'} in progress. Keep hunting!</span>`
          });
        } else {
          gameLogRef.current.addLogEntry({
            type: 'system',
            text: `<span style="color:#c8a030">📜 New bounties are available! Visit any <strong>Tavern</strong> to pick up daily &amp; weekly hunts.</span>`
          });
        }
      }
    } catch (err) {
      // Silent fail — HUD indicator just won't show
    }
  }, []);

  // Fetch bounty status on initial load
  useEffect(() => {
    if (gameState.character) {
      fetchBountyStatus();
    }
  }, [gameState.character?.id, fetchBountyStatus]);

  // Refresh bounty HUD after closing the bounty board (picks up accepts/claims)
  useEffect(() => {
    if (!showBountyBoard) {
      fetchBountyStatus();
    }
  }, [showBountyBoard, fetchBountyStatus]);

  // Refresh bounty HUD after closing the active bounties panel
  useEffect(() => {
    if (!showActiveBounties) {
      fetchBountyStatus();
    }
  }, [showActiveBounties, fetchBountyStatus]);

  // Refresh bounty HUD after battle (picks up kill progress)
  useEffect(() => {
    if (battleResult) return; // Wait until post-battle screen is closed
    // After a battle ends, the bounty counts may have changed
    const timer = setTimeout(() => fetchBountyStatus(), 1000);
    return () => clearTimeout(timer);
  }, [battleResult, fetchBountyStatus]);

  // Gathering handler
  const handleGather = useCallback(async () => {
    if (!gatherNode || isGathering || gatherCooldown > 0) return;
    setIsGathering(true);
    setGatherResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/crafting/gather`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ gatherType: gatherNode.type, tier: gatherNode.tier }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setGatherResult(data);
        // Start cooldown timer
        setGatherCooldown(10);
        // Log it
        if (gameLogRef.current) {
          const matStr = data.materials.map(m => `${m.qty}x ${m.name}`).join(', ');
          gameLogRef.current.addLogEntry({
            type: 'system',
            text: `⛏️ Gathered: ${matStr} (+${data.xpGained} ${data.discipline} XP)`
          });
        }
        refreshCharacterData();
      } else if (response.status === 429 && data.cooldownRemaining) {
        // Server cooldown — sync timer
        setGatherCooldown(Math.ceil(data.cooldownRemaining));
        if (gameLogRef.current) {
          gameLogRef.current.addLogEntry({ type: 'system', text: `⏳ Gathering cooldown: ${Math.ceil(data.cooldownRemaining)}s remaining` });
        }
      } else {
        if (gameLogRef.current) {
          gameLogRef.current.addLogEntry({ type: 'error', text: `Gathering failed: ${data.error || 'Unknown error'}` });
        }
      }
    } catch (err) {
      console.error('Gather error:', err);
    } finally {
      setIsGathering(false);
    }
  }, [gatherNode, isGathering, gatherCooldown, refreshCharacterData]);

  // Gather cooldown countdown timer
  useEffect(() => {
    if (gatherCooldown <= 0) return;
    const timer = setTimeout(() => setGatherCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [gatherCooldown]);

  // Keep showDelveRef in sync with the showDelve state so socket handlers
  // (which have stale closures) can always read the current value.
  useEffect(() => {
    showDelveRef.current = showDelve;
  }, [showDelve]);

  // Arena battle state tracking - keep modal open during PvP fights
  useEffect(() => {
    if (!socket) return;

    const onArenaBattleStarted = () => {
      setInArenaBattle(true);
      setShowArena(true);
    };
    const onArenaBattleEnded = () => {
      setInArenaBattle(false);
      refreshCharacterData();
    };

    socket.on('arena_battle_started', onArenaBattleStarted);
    socket.on('arena_battle_ended', onArenaBattleEnded);

    return () => {
      socket.off('arena_battle_started', onArenaBattleStarted);
      socket.off('arena_battle_ended', onArenaBattleEnded);
    };
  }, [socket, refreshCharacterData]);

  // Combat event listeners
  useEffect(() => {
    if (!socket) return;

    const handleMonsterSpotted = (data) => {
      setSpottedMonster(data);
      gameLogRef.current?.addLogEntry({
        type: 'encounter',
        text: data.message
      });
    };

    const handleCombatStarted = (data) => {
      lastBattleUpdateAtRef.current = Date.now();
      setCurrentBattle(data.battle);
      setSpottedMonster(null);
      setAttackPending(false);
      if (attackTimeoutRef.current) { clearTimeout(attackTimeoutRef.current); attackTimeoutRef.current = null; }
      setBattleMusic(data.music || 'battle.mp3');
      gameLogRef.current?.addLogEntry({
        type: 'combat',
        text: `Battle started with ${data.battle.monster.name}!`
      });
    };

    const handleBattleUpdate = (data) => {
      lastBattleUpdateAtRef.current = Date.now();
      setCurrentBattle(data.battle);
      const b = data.battle;
      trackAction('battle_update', `R${b?.round || '?'} ${b?.turn || '?'} HP:${b?.character?.currentHp || '?'}/${b?.character?.hp || '?'} MP:${b?.character?.currentMp ?? '?'} Mon:${b?.monster?.currentHp || '?'}/${b?.monster?.hp || '?'}${b?.deck ? ' AP:' + b.deck.ap + '/' + b.deck.maxAp + ' hand:' + (b.deck.hand?.length || 0) : ''}`);
      gameLogRef.current?.addLogEntry({
        type: 'combat',
        text: data.message
      });
    };

    const handleBattleEnded = async (data) => {
      lastBattleUpdateAtRef.current = Date.now();
      // Don't clear currentBattle immediately — the last battle_update contains
      // the killing-blow log entries that the player needs to see.  Stash the
      // end-of-battle payload; handleCloseCombat ("Continue" button) will
      // process it after the player has read the final log.  (Bug #250)
      pendingBattleEndRef.current = data;

      // Scenario A — Delve combat with DelveModal mounted:
      // CombatInterface (z-index 1000) is hidden behind DelveModal (z-index 1200),
      // so the player can never click "Continue" to dismiss it.  Clear currentBattle
      // immediately so DelveModal's prevBattleRef effect fires and calls the proper
      // Delve completion endpoint (/complete-node or /claim-boss-rewards).
      // onCharacterUpdate (refreshCharacterData) is called by that effect.
      if ((data.isDelveEncounter || data.isDelveBoss) && showDelveRef.current) {
        setCurrentBattle(null);
        pendingBattleEndRef.current = null; // consumed — handleCloseCombat won't be called
      }
    };

    const handleActionResult = (data) => {
      gameLogRef.current?.addLogEntry({
        type: data.success ? 'action' : 'error',
        text: data.message || data.result || 'Action performed.'
      });
    };

    // Handle area process events (e.g., town guards confronting wanted players)
    const handleAreaProcess = (data) => {
      // Display dialogue output
      if (data.output) {
        gameLogRef.current?.addLogEntry({
          type: 'conversation',
          text: data.output
        });
        // v672: surface portraits for tile-trigger convos in the JRPG popup
        gameLogRef.current?.setDialogueContext({
          speaker: data.npcDisplayName || null,
          npcDisplayName: data.npcDisplayName || null,
          npcPortrait: data.npcPortrait || null,
          playerPortrait: data.playerPortrait || null,
          text: data.output
        });
      }

      // Handle warp (e.g., being sent to jail)
      if (data.warp) {
        // requestGameState returns full character + maze data; no need to also call refreshCharacterData
        requestGameState();
      }

      // Handle battle trigger (e.g., attacking the guard)
      if (data.battle) {
        convoBattleCallbacksRef.current = {
          won: data.battle.won,
          lost: data.battle.lost,
          ran: data.battle.ran
        };
        setCurrentConversation(null);
        if (socket) {
          socket.emit('start_convo_battle');
        }
        return;
      }

      // Show conversation options (e.g., "Attack Him" / "Turn self in")
      if (data.options && Object.keys(data.options).length > 0) {
        setCurrentConversation({
          npc: 'process',
          output: data.output,
          options: data.options,
          ended: false
        });
        const choices = Object.entries(data.options).map(([nodeName, text]) => ({
          text: text,
          value: nodeName,
          meta: (data.optionMeta && data.optionMeta[nodeName]) || null
        }));
        gameLogRef.current?.setPendingChoices(choices);
      }
    };

    socket.on('monster_spotted', handleMonsterSpotted);
    socket.on('combat_started', handleCombatStarted);
    socket.on('battle_update', handleBattleUpdate);
    socket.on('battle_ended', handleBattleEnded);
    socket.on('action_result', handleActionResult);
    socket.on('area_process', handleAreaProcess);

    // Generic server errors (e.g. "Failed to join game", "Monster not found")
    const handleServerError = (data) => {
      setAttackPending(false);
      if (attackTimeoutRef.current) { clearTimeout(attackTimeoutRef.current); attackTimeoutRef.current = null; }
      gameLogRef.current?.addLogEntry({
        type: 'error',
        text: data?.message || 'An error occurred.'
      });
    };
    // Combat-specific errors (e.g. invalid action, combat action failed)
    const handleCombatError = (data) => {
      setAttackPending(false);
      if (attackTimeoutRef.current) { clearTimeout(attackTimeoutRef.current); attackTimeoutRef.current = null; }
      // Immediately release the action lock in CombatInterface so the player
      // doesn't have to wait for the 1-second safety timeout to retry.
      if (combatErrorRef.current && typeof combatErrorRef.current.resetLock === 'function') {
        combatErrorRef.current.resetLock();
      }
      trackAction('combat_error', data?.message || 'Combat action failed.');
      gameLogRef.current?.addLogEntry({
        type: 'error',
        text: data?.message || 'Combat action failed.'
      });
      // v379: If the server flagged battleLost, the in-memory battle is gone
      // (e.g. after a brief socket disconnect).  Auto-close the stale battle UI
      // so the player isn't stuck clicking into a dead session.
      if (data?.battleLost && currentBattleRef.current) {
        setCurrentBattle(null);
        pendingBattleEndRef.current = null;
        setBattleMusic(null);
      }
    };
    socket.on('error', handleServerError);
    socket.on('combat_error', handleCombatError);

    // AC: Scout and other game messages
    const handleGameMessage = (data) => {
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: data?.message || ''
      });
    };
    socket.on('game_message', handleGameMessage);

    // Overworld random events
    const handleOverworldEvent = (data) => {
      if (data?.event) {
        setOverworldEvent(data.event);
        setOverworldEventResult(null);
        setOverworldNextStep(null);
        gameLogRef.current?.addLogEntry({
          type: 'system',
          text: `📜 ${data.event.name}`
        });
      }
    };
    socket.on('overworld_event', handleOverworldEvent);

    // Treasure Map arrival — guaranteed treasure event + bonus loot
    const handleTreasureMapArrived = (data) => {
      if (data?.event) {
        setOverworldEvent(data.event);
        setOverworldEventResult(null);
        setOverworldNextStep(null);
        // Immediately clear the consumed treasure map from client state so the
        // HUD indicator and map icon disappear without waiting for a refresh.
        setGameState(prev => ({
          ...prev,
          character: prev.character ? { ...prev.character, treasure_map: null } : prev.character,
          maze: prev.maze ? { ...prev.maze, treasureMapTile: null } : prev.maze,
        }));
        // Build bonus summary
        const parts = [`💰 +${data.bonusGold}g`];
        if (data.forgeToken) parts.push(`🔨 Forge Token${data.forgeToken > 1 ? ` x${data.forgeToken}` : ''}`);
        if (data.delveToken) parts.push('🗝️ Delve Token');
        const arrRarity = data.rarity || 'common';
        const arrColor = { common: '#ffd700', rare: '#a855f7', legendary: '#ef4444' }[arrRarity] || '#ffd700';
        const arrLabel = { common: '', rare: 'Rare ', legendary: 'LEGENDARY ' }[arrRarity] || '';
        gameLogRef.current?.addLogEntry({
          type: 'system',
          text: `<span style="color:${arrColor}">🗺️ ${arrLabel}${data.message}</span>`
        });
        gameLogRef.current?.addLogEntry({
          type: 'system',
          text: `<span style="color:${arrColor}">Treasure: ${parts.join(' • ')}</span>`
        });
        gameLogRef.current?.addLogEntry({
          type: 'system',
          text: `📜 ${data.event.name}`
        });
        // Refresh to pick up the bonus gold/tokens in the UI
        refreshCharacterData();
      }
    };
    socket.on('treasure_map_arrived', handleTreasureMapArrived);

    const handleAtlasZoneDiscovered = (data) => {
      if (!data?.zone) return;

      const zoneReward = data.rewards?.zone || { gold: 0, xp: 0 };
      const regionReward = data.rewards?.region || null;
      const zoneParts = [];
      if (zoneReward.gold) zoneParts.push(`+${zoneReward.gold} gold`);
      if (zoneReward.xp) zoneParts.push(`+${zoneReward.xp} XP`);

      const zoneMessage = `${data.zone.icon || '🧭'} New Atlas zone discovered: ${data.zone.name}`;
      const zoneRewardText = zoneParts.length > 0 ? `Reward: ${zoneParts.join(' • ')}` : 'Reward claimed.';

      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: zoneMessage
      });
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: zoneRewardText
      });
      toast.success(`${zoneMessage}${zoneParts.length ? ` — ${zoneParts.join(', ')}` : ''}`, { duration: 5000 });

      if (data.regionCompleted && regionReward) {
        const regionParts = [];
        if (regionReward.gold) regionParts.push(`+${regionReward.gold} gold`);
        if (regionReward.xp) regionParts.push(`+${regionReward.xp} XP`);
        const regionMessage = `🏁 Region fully charted: ${data.region?.name || data.zone.regionName}`;

        gameLogRef.current?.addLogEntry({
          type: 'system',
          text: regionMessage
        });
        gameLogRef.current?.addLogEntry({
          type: 'system',
          text: `Bonus reward: ${regionParts.join(' • ')}`
        });
        toast.success(`${regionMessage} — ${regionParts.join(', ')}`, { duration: 6500 });
      }
    };
    socket.on('atlas_zone_discovered', handleAtlasZoneDiscovered);

    return () => {
      socket.off('monster_spotted', handleMonsterSpotted);
      socket.off('combat_started', handleCombatStarted);
      socket.off('battle_update', handleBattleUpdate);
      socket.off('battle_ended', handleBattleEnded);
      socket.off('action_result', handleActionResult);
      socket.off('area_process', handleAreaProcess);
      socket.off('error', handleServerError);
      socket.off('combat_error', handleCombatError);
      socket.off('game_message', handleGameMessage);
      socket.off('overworld_event', handleOverworldEvent);
      socket.off('treasure_map_arrived', handleTreasureMapArrived);
      socket.off('atlas_zone_discovered', handleAtlasZoneDiscovered);
    };
  }, [socket, refreshCharacterData]);

  const handleLogout = () => {
    logout();
  };

  const handleSwitchCharacter = () => {
    switchCharacter();
    navigate('/characters');
  };

  // Welcome-modal close handler: also fires a social-presence toast on first
  // login per character so newcomers see they're in a populated world.
  const handleWelcomeClose = useCallback(() => {
    setShowWelcome(false);
    try {
      const charId = gameState?.character?.id;
      if (!charId) return;
      const key = `loria_welcome_social_shown_${charId}`;
      if (localStorage.getItem(key)) return;
      const n = gameState?.onlinePlayers?.length || 0;
      if (n > 0) {
        toast.success(
          n === 1
            ? '🌍 1 other adventurer is exploring Loria right now — say hi in chat!'
            : `🌍 ${n} adventurers are exploring Loria right now — say hi in chat!`,
          { duration: 6000 }
        );
      }
      localStorage.setItem(key, '1');
    } catch (_) { /* localStorage unavailable */ }
  }, [gameState?.character?.id, gameState?.onlinePlayers]);

  const handleCombatAction = useCallback((action) => {
    if (socket) {
      trackAction('combat_action', `${action.type}${action.spell ? ': ' + action.spell : ''}${action.ability ? ': ' + action.ability : ''}${action.item ? ': ' + action.item : ''}${action.cardId ? ' card:' + action.cardId : ''}`);
      socket.emit('combat_action', action);
    }
  }, [socket, trackAction]);

  const [attackPending, setAttackPending] = useState(false);
  const attackTimeoutRef = useRef(null);

  const handleAttackMonster = useCallback((monsterName) => {
    if (!socket) {
      console.error('[Combat] Cannot attack — socket is null');
      gameLogRef.current?.addLogEntry({ type: 'error', text: 'Connection lost — cannot start battle.' });
      return;
    }
    console.log(`[Combat] Emitting attack_monster: ${monsterName}`);
    setAttackPending(true);
    socket.emit('attack_monster', monsterName);
    // Don't clear spottedMonster optimistically — wait for combat_started confirmation.
    // Set a timeout to recover if server never responds.
    if (attackTimeoutRef.current) clearTimeout(attackTimeoutRef.current);
    attackTimeoutRef.current = setTimeout(() => {
      setAttackPending(false);
      console.warn('[Combat] attack_monster timeout — server did not start combat within 5s');
      gameLogRef.current?.addLogEntry({ type: 'error', text: 'Battle failed to start. Try again or move away.' });
    }, 5000);
  }, [socket]);

  const handleCloseCombat = useCallback(async () => {
    setCurrentBattle(null);

    // Process stashed battle_ended data (Bug #250: deferred so final-blow log is visible)
    const data = pendingBattleEndRef.current;
    pendingBattleEndRef.current = null;

    if (!data) {
      // Safety fallback: no pending data (e.g. closed manually), just clean up music
      setBattleMusic(null);
      return;
    }

    // Scenario B — Delve combat when DelveModal was NOT open during combat
    // (player pressed Escape before or during the fight, making CombatInterface
    // visible and reachable).  The player clicked "Continue" normally, so we call
    // the proper Delve completion endpoint here before the standard post-battle
    // flow so the node/run is finalized before DelveModal auto-reopens via the tile trigger.
    if (data.isDelveEncounter || data.isDelveBoss) {
      try {
        const token = localStorage.getItem('token');
        const delvePath = data.isDelveBoss ? '/api/delve/claim-boss-rewards' : '/api/delve/complete-node';
        await fetch(`${API_BASE_URL}${delvePath}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { /* ignore — node may already be completed or rewards already claimed */ }
    }

    gameLogRef.current?.addLogEntry({
      type: 'combat',
      text: data.message || `Battle ended: ${data.status}`
    });

    // Notify about pet events
    if (data.status === 'charmed' && data.pet) {
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: `<span style="color:#f39c12">🐾 ${data.pet.name} is now your loyal pet companion!</span>`
      });
    }
    if (data.petDied) {
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: `<span style="color:#e74c3c">💀 Your pet was slain in battle!</span>`
      });
    }

    // AC Quest progress notification
    if (data.acQuestMessage) {
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: `<span style="color:#ffd700">${data.acQuestMessage}</span>`
      });
    }

    // Bounty Board progress notification
    if (data.bountyProgress && data.bountyProgress.length > 0) {
      for (const bp of data.bountyProgress) {
        const color = bp.complete ? '#2ecc71' : '#f0c040';
        const msg = bp.complete
          ? `📜 Bounty complete! <strong>${bp.monster}</strong> — return to a Tavern to claim your reward!`
          : `📜 Bounty: <strong>${bp.monster}</strong> ${bp.current}/${bp.target}`;
        gameLogRef.current?.addLogEntry({
          type: 'system',
          text: `<span style="color:${color}">${msg}</span>`
        });
      }
    }

    // Treasure Map found notification
    if (data.treasureMapFound) {
      const tm = data.treasureMapFound;
      const mapRarity = tm.rarity || 'common';
      const rarityLabel = { common: '', rare: 'Rare ', legendary: 'Legendary ' }[mapRarity] || '';
      const rarityColor = { common: '#ffd700', rare: '#a855f7', legendary: '#ef4444' }[mapRarity] || '#ffd700';
      // Format area name for display (strip .dat, replace digits at end)
      const areaLabel = (tm.area || '').replace(/\.dat$/, '').replace(/\d+$/, '');
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: `<span style="color:${rarityColor}">🗺️ You found a <strong>${rarityLabel}Treasure Map</strong>! It points to a location in <strong>${areaLabel}</strong>. Check your minimap!</span>`
      });
    }

    // Refresh full game state to get updated XP/gold/inventory/maze after battle.
    // MUST await — otherwise PostBattleScreen renders with stale data,
    // causing didLevelUp to be false and skipping the allocation screen.
    await refreshCharacterData();

    // Check if this was a conversation-triggered battle
    const callbacks = convoBattleCallbacksRef.current;
    if (callbacks) {
      convoBattleCallbacksRef.current = null;
      let nextNode = null;
      if ((data.status === 'victory' || data.status === 'charmed') && callbacks.won) {
        nextNode = callbacks.won;
      } else if (data.status === 'defeat' && callbacks.lost) {
        nextNode = callbacks.lost;
      } else if (data.status === 'fled' && callbacks.ran) {
        nextNode = callbacks.ran;
      }

      if (nextNode) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/conversation/continue`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ optionNode: nextNode })
          });

          if (response.ok) {
            const conversationData = await response.json();
            if (conversationData.output) {
              gameLogRef.current?.addLogEntry({
                type: 'conversation',
                text: conversationData.output
              });
              // v665: also surface the NPC's response in the JRPG portrait
              // popup so post-battle dialogue isn't easy to miss.
              gameLogRef.current?.setDialogueContext({
                speaker: conversationData.speaker || conversationData.npcDisplayName || null,
                npcDisplayName: conversationData.npcDisplayName || null,
                npcPortrait: conversationData.npcPortrait || null,
                playerPortrait: conversationData.playerPortrait || null,
                text: conversationData.output
              });
            }
            if (!conversationData.ended && conversationData.options && Object.keys(conversationData.options).length > 0) {
              setCurrentConversation(conversationData);
              const choices = Object.entries(conversationData.options).map(([nodeName, text]) => ({
                text: text,
                value: nodeName,
                meta: (conversationData.optionMeta && conversationData.optionMeta[nodeName]) || null
              }));
              gameLogRef.current?.setPendingChoices(choices);
            }
          }
        } catch (err) {
          console.error('Error continuing conversation after battle:', err);
        }
      }
    }

    // Show post-battle screen for victory/defeat (skip for fled/charmed)
    if (data.status === 'victory' || data.status === 'defeat') {
      setBattleResult({
        status: data.status,
        rewards: data.rewards || null,
        message: data.message || null,
        music: data.music || null,
        deckStats: data.deckStats || null
      });
    } else {
      setBattleMusic(null);
    }
  }, [refreshCharacterData]);

  const handleClosePostBattle = useCallback(() => {
    setBattleResult(null);
    setBattleMusic(null);
    // Request a full game state refresh (character + maze + nearby players).
    // Critical after death: the server teleported us to a new area, so we
    // need the correct maze data.  Without this, there's a race condition
    // where the 2-second delayed sendInitialGameState hasn't arrived yet.
    requestGameState();
  }, [requestGameState]);

  // Detect inventory overflow from game_state — show / auto-close discard modal.
  // Triggers after battle (game_state refreshed), salvage, craft, or on reconnect.
  //
  // NOTE: onResolved() does NOT call setInventoryOverflow(null) directly because
  // that causes a race: clearing inventoryOverflow makes the guard below pass on
  // the very next render while gameState is still stale (items still have _new
  // flags and count is still over capacity), so the modal would immediately
  // re-open with the old snapshot.  Instead, onResolved() only fires
  // refreshCharacterData().  This effect then auto-closes the modal once the
  // refreshed gameState shows a clean inventory (no _new flags, within capacity).
  useEffect(() => {
    if (!gameState?.character) return;
    if (currentBattle || battleResult) return; // Don't interrupt combat or post-battle screen

    const inv = gameState.character.inventory;
    if (!Array.isArray(inv)) return;

    const equip = gameState.character.equipment || {};
    const capacity = 20 + (equip.backpack?.size || 0);
    const hasNewItems = inv.some(item => item._new);

    if (inventoryOverflow) {
      // Auto-close when inventory is now within capacity or has no new items
      if (inv.length <= capacity || !hasNewItems) {
        setInventoryOverflow(null);
      }
      return;
    }

    if (inv.length > capacity && hasNewItems) {
      setInventoryOverflow({ inventory: inv, maxCapacity: capacity });
    }
  }, [gameState, inventoryOverflow, currentBattle, battleResult]);

  // Use item out of combat via REST API
  const handleUseItem = useCallback(async (itemName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player/use-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ itemName })
      });
      const data = await response.json();
      if (response.ok) {
        gameLogRef.current?.addLogEntry({
          type: 'action',
          text: data.message || `Used ${itemName}.`
        });
        // Handle warp scroll teleportation — need full game state reload for new maze
        if (data.teleported) {
          requestGameState();
          // Safety-net: if game_state doesn't arrive within 3s, retry once.
          // The server now also pushes game_state proactively, but a retry
          // guards against socket hiccups or rate-limiting.
          setTimeout(() => requestGameState(), 3000);
          gameLogRef.current?.addLogEntry({
            type: 'system',
            text: 'You have been teleported to a new area!'
          });
        } else {
          refreshCharacterData();
        }
      } else {
        gameLogRef.current?.addLogEntry({
          type: 'error',
          text: data.error || `Cannot use ${itemName}.`
        });
      }
    } catch (err) {
      gameLogRef.current?.addLogEntry({
        type: 'error',
        text: 'Failed to use item - network error.'
      });
    }
  }, [refreshCharacterData, requestGameState]);

  // Teleport handler for warp scrolls used from modal panels (Inventory/CharacterSheet)
  const handleTeleport = useCallback(() => {
    setShowInventory(false);
    setShowCharacterSheet(false);
    requestGameState();
    // Safety-net: retry after 3s in case the first request was rate-limited
    setTimeout(() => requestGameState(), 3000);
    gameLogRef.current?.addLogEntry({
      type: 'system',
      text: 'You have been teleported to a new area!'
    });
  }, [requestGameState]);

  // Cast a friendly spell on self (out of combat) via REST API
  const handleCastSpell = useCallback(async (spellName, extraBody = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player/cast-spell`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ spellName, ...extraBody })
      });
      const data = await response.json();
      if (response.ok) {
        // Warp II choice prompt — show modal instead of logging
        if (data.warpChoice) {
          setWarpChoice({
            spellName,
            warpType: data.warpType || 'mark',
            hasExistingMark: data.hasExistingMark,
            markLabel: data.markLabel,
            waypoints: data.waypoints || [],
            maxWaypoints: data.maxWaypoints || 3,
          });
          return;
        }
        gameLogRef.current?.addLogEntry({
          type: 'action',
          text: data.message || `Cast ${spellName}.`
        });
        // Handle teleportation (e.g., travel spells) — need full game state reload for new maze
        if (data.teleported) {
          requestGameState();
          // Safety-net: retry after 3s in case the first request was rate-limited
          setTimeout(() => requestGameState(), 3000);
          gameLogRef.current?.addLogEntry({
            type: 'system',
            text: 'You have been teleported!'
          });
        } else {
          refreshCharacterData();
        }
      } else {
        gameLogRef.current?.addLogEntry({
          type: 'error',
          text: data.error || `Cannot cast ${spellName}.`
        });
      }
    } catch (err) {
      gameLogRef.current?.addLogEntry({
        type: 'error',
        text: 'Failed to cast spell - network error.'
      });
    }
  }, [refreshCharacterData, requestGameState]);

  // Cast an offensive spell at a monster - starts combat with spell as opener
  const handleCastSpellAtMonster = useCallback((spellName, monsterName) => {
    if (!socket) {
      console.error('[Combat] Cannot cast spell at monster — socket is null');
      gameLogRef.current?.addLogEntry({ type: 'error', text: 'Connection lost — cannot cast spell.' });
      return;
    }
    setAttackPending(true);
    socket.emit('cast_spell_at_monster', { spellName, monsterName });
    // Don't clear spottedMonster — combat_started will clear it
    if (attackTimeoutRef.current) clearTimeout(attackTimeoutRef.current);
    attackTimeoutRef.current = setTimeout(() => {
      setAttackPending(false);
      gameLogRef.current?.addLogEntry({ type: 'error', text: 'Spell attack failed to start. Try again.' });
    }, 5000);
    gameLogRef.current?.addLogEntry({
      type: 'combat',
      text: `Casting ${spellName} at ${monsterName}!`
    });
  }, [socket]);

  // Cast a friendly spell on another player via REST API
  const handleCastSpellOnPlayer = useCallback(async (spellName, targetCharacterId, targetName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player/cast-spell-on-player`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ spellName, targetCharacterId })
      });
      const data = await response.json();
      if (response.ok) {
        gameLogRef.current?.addLogEntry({
          type: 'action',
          text: data.message || `Cast ${spellName} on ${targetName}.`
        });
        refreshCharacterData();
      } else {
        gameLogRef.current?.addLogEntry({
          type: 'error',
          text: data.error || `Cannot cast ${spellName} on ${targetName}.`
        });
      }
    } catch (err) {
      gameLogRef.current?.addLogEntry({
        type: 'error',
        text: 'Failed to cast spell on player - network error.'
      });
    }
  }, [refreshCharacterData]);

  // Use a restorative item on another player via REST API
  const handleUseItemOnPlayer = useCallback(async (itemName, targetCharacterId, targetName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player/use-item-on-player`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ itemName, targetCharacterId })
      });
      const data = await response.json();
      if (response.ok) {
        gameLogRef.current?.addLogEntry({
          type: 'action',
          text: data.message || `Used ${itemName} on ${targetName}.`
        });
        refreshCharacterData();
      } else {
        gameLogRef.current?.addLogEntry({
          type: 'error',
          text: data.error || `Cannot use ${itemName} on ${targetName}.`
        });
      }
    } catch (err) {
      gameLogRef.current?.addLogEntry({
        type: 'error',
        text: 'Failed to use item on player - network error.'
      });
    }
  }, [refreshCharacterData]);

  // Generic log message handler for child components
  const handleLogMessage = useCallback((msg) => {
    gameLogRef.current?.addLogEntry({ type: 'action', text: msg });
  }, []);

  // Dismiss pet (Druid out-of-combat)
  const handleDismissPet = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player/dismiss-pet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        gameLogRef.current?.addLogEntry({ type: 'action', text: `<span style="color:#f39c12">🐾 ${data.message}</span>` });
        refreshCharacterData();
      } else {
        gameLogRef.current?.addLogEntry({ type: 'error', text: data.error || 'Failed to dismiss pet.' });
      }
    } catch (err) {
      console.error('Dismiss pet error:', err);
      gameLogRef.current?.addLogEntry({ type: 'error', text: 'Failed to dismiss pet - network error.' });
    }
  }, [refreshCharacterData]);

  // Handle automatic ConvoCode execution when stepping on NPC tiles
  const handleNpcEncounter = useCallback(async (npcId, x, y) => {
    if (currentConversation?.npc === npcId) {
      return; // Already in conversation with this NPC
    }

    // Skip if we just finished a conversation on this exact tile
    if (lastNpcTileRef.current === `${x},${y}`) {
      return;
    }

    // Don't start new conversations during convo-triggered battles
    if (convoBattleCallbacksRef.current) {
      return;
    }

    try {
      console.log('Starting conversation with:', npcId, 'at position:', x, y);

      const response = await fetch(`${API_BASE_URL}/api/conversation/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ npcId, x, y })
      });

      console.log('Conversation API response status:', response.status);

      if (response.ok) {
        const conversationData = await response.json();
        console.log('Conversation data received:', conversationData);
        
        // Display the NPC's dialogue
        if (conversationData.output) {
          gameLogRef.current?.addLogEntry({
            type: 'conversation', 
            text: conversationData.output
          });
        }

        // Push portrait/dialogue context to GameLog (JRPG-style overlay)
        gameLogRef.current?.setDialogueContext({
          speaker: conversationData.speaker || conversationData.npcDisplayName || null,
          npcDisplayName: conversationData.npcDisplayName || null,
          npcPortrait: conversationData.npcPortrait || null,
          playerPortrait: conversationData.playerPortrait || null,
          text: conversationData.output || ''
        });

        // Check if conversation ended immediately
        if (conversationData.ended) {
          setCurrentConversation(null);
          // v665: If the NPC had something to say (output), leave the JRPG
          // portrait popup visible so the player can read it.  Movement or
          // the × button will dismiss it.  Only clear if there was no text.
          if (!conversationData.output) {
            gameLogRef.current?.clearDialogue?.();
          }
          lastNpcTileRef.current = `${x},${y}`;
          // If the NPC opened the crafting panel
          if (conversationData.craftOpen) {
            setShowCrafting(true);
          }
        } else if (conversationData.battle) {
          // Conversation triggered a fight
          const battle = conversationData.battle;
          // Store the won/lost/ran callback nodes for after combat
          convoBattleCallbacksRef.current = { won: battle.won, lost: battle.lost, ran: battle.ran };
          lastNpcTileRef.current = `${x},${y}`; // Prevent NPC re-trigger during battle
          setCurrentConversation(null);
          gameLogRef.current?.clearDialogue?.();
          // Tell the server to start actual combat (battle data stored server-side)
          if (socket) {
            socket.emit('start_convo_battle');
          }
        } else {
          setCurrentConversation(conversationData);
          
          // Show conversation options if available
          if (conversationData.options && Object.keys(conversationData.options).length > 0) {
            const choices = Object.entries(conversationData.options).map(([nodeName, text]) => ({
              text: text,
              value: nodeName,
              meta: (conversationData.optionMeta && conversationData.optionMeta[nodeName]) || null
            }));
            gameLogRef.current?.setPendingChoices(choices);
          }
        }
      } else if (response.status === 404) {
        // No conversation data for this NPC - silently ignore (decorative NPC)
        console.log('No conversation data for NPC:', npcId, '(404)');
        lastNpcTileRef.current = `${x},${y}`; // Don't retry on this tile
      } else {
        const errorText = await response.text();
        console.error('Conversation API error:', response.status, errorText);
        gameLogRef.current?.addLogEntry({
          type: 'system',
          text: `Could not start this conversation. (${response.status})`
        });
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: 'Unable to start conversation - network error.'
      });
    }
  }, [currentConversation, socket]);

  // Handle conversation choices
  const closeConversation = useCallback(() => {
    // v665: also fires for "ended-with-output" popups that have no
    // currentConversation tracked but still show the dialogue overlay.
    const hadActiveConversation = !!currentConversation;
    setCurrentConversation(null);
    gameLogRef.current?.clearDialogue?.();
    if (gameState.character) {
      lastNpcTileRef.current = `${gameState.character.x_position},${gameState.character.y_position}`;
    }
    if (!hadActiveConversation) return; // nothing to clean up server-side
    // Tell the server to clean up the conversation session (fire-and-forget)
    fetch(`${API_BASE_URL}/api/conversation/end`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    }).catch(() => { /* swallow — UI is already closed */ });
  }, [currentConversation, gameState.character]);

  const handleConversationChoice = useCallback(async (choice) => {
    if (!currentConversation) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/conversation/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          optionNode: choice.value
        })
      });

      if (response.ok) {
        const conversationData = await response.json();
        
        // Clear pending choices first (null not [] — empty array is truthy and leaves prompt visible)
        gameLogRef.current?.setPendingChoices(null);
        
        // Add the player's choice to the log
        gameLogRef.current?.addLogEntry({
          type: 'player_choice',
          text: choice.text
        });
        
        // Display NPC's response
        if (conversationData.output) {
          gameLogRef.current?.addLogEntry({
            type: 'conversation',
            text: conversationData.output
          });
        }

        // Update portrait dialogue context with the new beat
        gameLogRef.current?.setDialogueContext({
          speaker: conversationData.speaker || conversationData.npcDisplayName || null,
          npcDisplayName: conversationData.npcDisplayName || null,
          npcPortrait: conversationData.npcPortrait || null,
          playerPortrait: conversationData.playerPortrait || null,
          text: conversationData.output || '',
          lastChoiceText: choice.text
        });

        // Check if conversation ended
        if (conversationData.ended) {
          setCurrentConversation(null);
          // v665: keep the JRPG portrait popup visible if the NPC had a
          // closing line, so the player isn't left wondering what was said.
          // Movement or × dismisses it.
          if (!conversationData.output) {
            gameLogRef.current?.clearDialogue?.();
          }
          // Mark this tile so we don't re-trigger
          if (gameState.character) {
            lastNpcTileRef.current = `${gameState.character.x_position},${gameState.character.y_position}`;
          }
          
          // Handle warp (e.g., being sent to jail)
          if (conversationData.warp) {
            requestGameState();
          }
          
          // If the NPC opened the crafting panel
          if (conversationData.craftOpen) {
            setShowCrafting(true);
          }
          
          // Refresh character data to get updated quest information
          // (debounced, so this won't double-fire if requestGameState also ran)
          await refreshCharacterData();
        } else if (conversationData.warp) {
          // Warp happened mid-conversation (e.g., turn self in to guard)
          setCurrentConversation(null);
          gameLogRef.current?.clearDialogue?.();
          requestGameState();
          await refreshCharacterData();
        } else if (conversationData.battle) {
          // Conversation triggered a fight
          const battle = conversationData.battle;
          // Store the won/lost/ran callback nodes for after combat
          convoBattleCallbacksRef.current = { won: battle.won, lost: battle.lost, ran: battle.ran };
          // Lock NPC tile to prevent re-trigger during battle
          if (gameState.character) {
            lastNpcTileRef.current = `${gameState.character.x_position},${gameState.character.y_position}`;
          }
          gameLogRef.current?.setPendingChoices(null); // Clear prompt before combat
          setCurrentConversation(null);
          gameLogRef.current?.clearDialogue?.();
          // Tell the server to start actual combat (battle data stored server-side)
          if (socket) {
            socket.emit('start_convo_battle');
          } else {
            console.error('[ConvoBattle] Socket is null — cannot emit start_convo_battle');
          }
        } else {
          // Update current conversation
          setCurrentConversation(conversationData);
          
          // Show new options if available
          if (conversationData.options && Object.keys(conversationData.options).length > 0) {
            const choices = Object.entries(conversationData.options).map(([nodeName, text]) => ({
              text: text,
              value: nodeName,
              meta: (conversationData.optionMeta && conversationData.optionMeta[nodeName]) || null
            }));
            gameLogRef.current?.setPendingChoices(choices);
          }
        }
      }
    } catch (error) {
      console.error('Error continuing conversation:', error);
      gameLogRef.current?.addLogEntry({
        type: 'system',
        text: 'Error in conversation.'
      });
    }
  }, [currentConversation, socket]);

  // Check for NPC encounters when character position changes
  useEffect(() => {
    if (gameState.character && gameState.maze && !currentConversation && !currentBattle) {
      const { x_position, y_position } = gameState.character;
      const currentTile = `${x_position},${y_position}`;
      
      // Clear the last NPC tile lock if we've moved to a different tile
      if (lastNpcTileRef.current && lastNpcTileRef.current !== currentTile) {
        lastNpcTileRef.current = null;
      }

      const tile = gameState.maze.grid?.[y_position]?.[x_position];
      
      if (tile?.character) {
        handleNpcEncounter(tile.character, x_position, y_position);
      }
    }
  }, [gameState.character?.x_position, gameState.character?.y_position, gameState.maze, handleNpcEncounter, currentConversation, currentBattle]);

  // Check for shop tiles when character position changes
  useEffect(() => {
    if (gameState.character && gameState.maze) {
      const { x_position, y_position } = gameState.character;
      const tile = gameState.maze.grid?.[y_position]?.[x_position];
      
      const onShop = !!(tile?.shop);
      const onMarket = !!(tile?.market);
      const onArena = !!(tile?.arena);
      const onCraft = !!(tile?.craft);
      const onStash = !!(tile?.stash);
      const onBounty = !!(tile?.bountyBoard);
      const onDelve  = !!(tile?.delve);
      setIsOnShopTile(onShop);
      setIsOnMarketTile(onMarket);
      setIsOnArenaTile(onArena);
      setIsOnCraftTile(onCraft);
      setIsOnStashTile(onStash);
      setIsOnBountyTile(onBounty);
      setIsOnDelveTile(onDelve);

      // Auto-open shop when stepping on a shop tile (if not in combat/convo)
      if (onShop && !currentBattle && !currentConversation && !spottedMonster && !showShop) {
        setShowShop(true);
      }
      // Close shop if we move off a shop tile
      if (!onShop && showShop) {
        setShowShop(false);
      }
      // Auto-open market when stepping on a market tile
      if (onMarket && !currentBattle && !currentConversation && !spottedMonster && !showMarket) {
        setShowMarket(true);
      }
      // Close market if we move off a market tile
      if (!onMarket && showMarket) {
        setShowMarket(false);
      }
      // Auto-open arena when stepping on an arena tile
      if (onArena && !currentBattle && !currentConversation && !spottedMonster && !showArena) {
        setShowArena(true);
      }
      // Close arena if we move off an arena tile (but not during a PvP battle)
      if (!onArena && showArena && !inArenaBattle) {
        setShowArena(false);
      }
      // Auto-open crafting when stepping on a craft tile
      if (onCraft && !currentBattle && !currentConversation && !spottedMonster && !showCrafting) {
        setShowCrafting(true);
      }
      // Close crafting if we move off a craft tile
      if (!onCraft && showCrafting) {
        setShowCrafting(false);
      }
      // Auto-open stash when stepping on a stash tile
      if (onStash && !currentBattle && !currentConversation && !spottedMonster && !showStash) {
        setShowStash(true);
      }
      // Close stash if we move off a stash tile
      if (!onStash && showStash) {
        setShowStash(false);
      }
      // Auto-open bounty board when stepping on a bounty tile
      if (onBounty && !currentBattle && !currentConversation && !spottedMonster && !showBountyBoard) {
        setShowBountyBoard(true);
      }
      // Close bounty board if we move off a bounty tile
      if (!onBounty && showBountyBoard) {
        setShowBountyBoard(false);
      }

      // Auto-open Delve modal when stepping on a delve entrance tile
      if (onDelve && !currentBattle && !currentConversation && !spottedMonster && !showDelve) {
        setShowDelve(true);
      }
      // Close Delve modal if we move off the delve tile
      if (!onDelve && showDelve) {
        setShowDelve(false);
      }

      // Detect campfire adjacency (cardinal neighbors only)
      const nearCampfire = [
        { x: x_position, y: y_position - 1 },
        { x: x_position, y: y_position + 1 },
        { x: x_position - 1, y: y_position },
        { x: x_position + 1, y: y_position },
      ].some(n => {
        const t = gameState.maze.grid?.[n.y]?.[n.x];
        return t && t.campfire;
      });
      setIsNearCampfire(nearCampfire);

      // Detect gathering node tiles
      if (tile?.gather) {
        // Determine tier from the map area name
        const area = gameState.character.current_area || '';
        let tier = 'low';
        if (/lich|necro|dragon|infern|elder|demon|abyss|fireriver|^df\d/i.test(area)) {
          tier = 'high';
        } else if (/stonecave|jungle|temple|darkmaze/i.test(area)) {
          tier = 'mid';
        } else {
          // For numbered areas, deeper = higher tier
          const numMatch = area.match(/(\d+)/);
          const areaNum = numMatch ? parseInt(numMatch[1]) : 0;
          if (/^tcave/i.test(area) && areaNum >= 10) tier = 'mid';
          else if (/^maze/i.test(area) && areaNum >= 15) tier = 'mid';
        }
        setGatherNode({ type: tile.gather, tier });
        setGatherResult(null);
      } else {
        setGatherNode(null);
        setGatherResult(null);
      }
    }
  }, [gameState.character?.x_position, gameState.character?.y_position, gameState.maze]);

  // Detect pending level-ups (XP says higher level than stored level)
  useEffect(() => {
    const char = gameState.character;
    if (char && char.experience != null && char.level != null) {
      const trueLevel = characterLevel(char.experience);
      if (trueLevel > char.level) {
        setPendingLevelUp(true);
      } else {
        setPendingLevelUp(false);
      }
    }
  }, [gameState.character?.experience, gameState.character?.level]);

  // Detect Advanced Class selection needed (level 40+ with no AC chosen)
  useEffect(() => {
    const char = gameState.character;
    if (char && char.experience != null && char.level != null) {
      const trueLevel = characterLevel(char.experience);
      const effectiveLevel = Math.max(trueLevel, char.level);
      if (effectiveLevel >= 40 && !char.advanced_class) {
        setPendingACSelection(true);
      } else {
        setPendingACSelection(false);
      }
    }
  }, [gameState.character?.experience, gameState.character?.level, gameState.character?.advanced_class]);

  if (!gameState.character) {
    return (
      <div className="loading">
        Loading game world...
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // MOBILE GAME LAYOUT — Full-screen map with overlay HUD (v514)
  // ════════════════════════════════════════════════════════════════
  if (isMobile) {
    const character = gameState.character;
    const xpForLvl = (lvl) => Math.ceil(((2 * lvl - 1) ** 2 - 1) / 8) * 1000;
    const level = characterLevel(character.experience);
    const xpThisLevel = xpForLvl(level);
    const xpNextLevel = xpForLvl(level + 1);
    const xpIntoLevel = character.experience - xpThisLevel;
    const xpNeeded = xpNextLevel - xpThisLevel;
    const xpPct = xpNeeded > 0 ? (xpIntoLevel / xpNeeded) * 100 : 100;
    const currentHp = character.max_hp - character.damage;
    const currentMp = character.max_mp - character.used_mp;
    const hpPct = character.max_hp > 0 ? (currentHp / character.max_hp) * 100 : 0;
    const mpPct = character.max_mp > 0 ? (currentMp / character.max_mp) * 100 : 0;

    // Parse active buffs for campfire HUD
    let mActiveBuffs = character.active_buffs;
    try { mActiveBuffs = typeof mActiveBuffs === 'string' ? JSON.parse(mActiveBuffs) : (mActiveBuffs || []); } catch(e) { mActiveBuffs = []; }
    const mCampfireBuff = mActiveBuffs.find(b => b.type === 'campfire_xp' && b.expiresAt && Date.now() < b.expiresAt);

    // Notification badge count for menu button
    const menuBadgeCount = unreadMessageCount + (bountyHudInfo?.claimable || 0);

    return (
      <CharacterProvider character={character}>
      <div className="m-game-container">
        {/* Always-mounted music player so audio doesn't restart whenever the
            mobile menu opens/closes. The visible UI lives in the drawer
            (.m-menu-music) which uses CSS to surface this same instance. */}
        <div className="m-music-host" style={{ position: 'absolute', left: -9999, top: -9999, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
          <MusicPlayer musicFile={gameState.maze?.music} paused={!!currentBattle || !!battleResult || !!gameState.fellowshipBattle} />
        </div>
        {/* ── Welcome Modal (first-time) ── */}
        {showWelcome && <WelcomeModal onClose={handleWelcomeClose} characterId={character?.id} />}
        {/* ── Contextual Hints ── */}
        <HintBanner character={character} inCombat={!!currentBattle} hasQuest={gameState.questSummary?.active > 0} hasLevelUp={pendingLevelUp} onOpenGuide={openProgressionGuide} />
        {/* ── Quest Tracker (mobile fullscreen layout) ── */}
        {!currentBattle && (
          <QuestTracker
            questSummary={gameState.questSummary}
            onClick={() => setShowCompendium(true)}
            style={{ top: 56 }}
          />
        )}
        {/* ── Full-Screen Map ── */}
        <div className="m-map-fullscreen">
          <MapGrid
            character={character}
            maze={gameState.maze}
            nearbyPlayers={gameState.nearbyPlayers}
            onPlayerClick={(id) => setInspectTargetId(id)}
            viewCols={9}
            viewRows={5}
            gridLayout={true}
            onMove={(dir) => {
              if (!currentBattle && !gameState.fellowshipBattle && !overworldEvent) {
                if (currentConversation) { setCurrentConversation(null); gameLogRef.current?.setPendingChoices(null); }
                setSpottedMonster(null);
                movePlayer(dir);
              }
            }}
          />
        </div>

        {/* ── HUD Overlay ── */}
        <div className="m-hud">
          {/* Top-Left: Menu Button */}
          <div className="m-menu-btn-wrapper">
            <button className="m-menu-btn" onClick={() => setShowMobileMenu(true)}>☰</button>
            {menuBadgeCount > 0 && (
              <span className="m-menu-btn-badge">{menuBadgeCount > 9 ? '9+' : menuBadgeCount}</span>
            )}
          </div>

          {/* Top-Center: Area Name */}
          <div className="m-area-banner">
            🏰 {(character.current_area || 'Unknown').replace('.dat', '').replace(/(\d+)$/, ' $1').replace(/^\w/, c => c.toUpperCase())}
          </div>

          {/* Top-Right: World Map + Chat + Connection */}
          <div className="m-top-right">
            <div className="m-connection-dot" style={{ color: isConnected ? '#4ade80' : '#ef4444' }} />
            <button className="m-chat-btn" onClick={() => setShowMobileChat(true)}>💬</button>
            <button className="m-worldmap-btn" onClick={async () => {
              if (generatingWorldMap) return;
              setGeneratingWorldMap(true);
              try {
                const token = localStorage.getItem('token');
                const resp = await fetch(`${API_BASE_URL}/api/game/worldmap?size=8`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (!resp.ok) throw new Error('Failed to generate map');
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
              } catch (err) {
                console.error('World map error:', err);
              } finally {
                setGeneratingWorldMap(false);
              }
            }}>
              {generatingWorldMap ? '⏳' : '🗺️'}
            </button>
          </div>

          {/* Left Side: Transparent Game Log */}
          <div className="m-log-overlay">
            <GameLog
              ref={gameLogRef}
              character={character}
              onConversationChoice={handleConversationChoice}
              onConversationClose={closeConversation}
            />
          </div>

          {/* Treasure Map HUD */}
          {character.treasure_map && (() => {
            const tmRarity = character.treasure_map.rarity || 'common';
            const tmRarityLabel = { common: '', rare: 'Rare ', legendary: 'Legendary ' }[tmRarity] || '';
            return (
              <div className="m-treasure-hud">
                <div className={`treasure-map-hud treasure-hud-${tmRarity}`}>
                  <span className="treasure-map-hud-icon">🗺️</span>
                  <span className="treasure-map-hud-text">
                    {tmRarityLabel && <strong>{tmRarityLabel}</strong>}
                    {character.treasure_map.area === character.current_area
                      ? 'Treasure nearby!'
                      : `Map → ${(character.treasure_map.area || '').replace(/\.dat$/, '').replace(/\d+$/, '')}`}
                  </span>
                  <button className="treasure-map-hud-discard" title="Discard" onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      await fetch(`${API_BASE_URL}/api/treasure-map/discard`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                      gameLogRef.current?.addLogEntry({ type: 'system', text: '🗺️ Treasure map discarded.' });
                      setGameState(prev => ({
                        ...prev,
                        character: prev.character ? { ...prev.character, treasure_map: null } : prev.character,
                        maze: prev.maze ? { ...prev.maze, treasureMapTile: null } : prev.maze,
                      }));
                      refreshCharacterData();
                    } catch (err) {
                      gameLogRef.current?.addLogEntry({ type: 'error', text: 'Failed to discard map.' });
                    }
                  }}>✕</button>
                </div>
              </div>
            );
          })()}

          {/* Campfire XP Buff HUD */}
          {mCampfireBuff && (
            <div className="m-buff-hud">
              <div className="campfire-buff-hud">
                <span className="campfire-buff-icon">🔥</span>
                <span className="campfire-buff-text">+{mCampfireBuff.value || 10}% XP ({Math.ceil((mCampfireBuff.expiresAt - Date.now()) / 60000)}m)</span>
              </div>
            </div>
          )}

          {/* ── Bottom HUD ── */}
          <div className="m-bottom-hud">
            {/* Portrait + HP/MP */}
            <div className="m-portrait-area">
              <div className="m-portrait-frame" onClick={() => setShowCharacterSheet(true)} style={{ cursor: 'pointer' }}>
                {resolveCharacterPortrait(character) ? (
                  <img src={resolveCharacterPortrait(character)} alt="" className="m-portrait-img" />
                ) : (
                  <span className="m-portrait-placeholder">🧑</span>
                )}
              </div>
              <div className="m-vital-bars">
                <div className="m-vital-bar">
                  <div className="m-vital-bar-fill hp" style={{ width: `${hpPct}%` }} />
                  <div className="m-vital-bar-text">{currentHp}/{character.max_hp}</div>
                </div>
                <div className="m-vital-bar">
                  <div className="m-vital-bar-fill mp" style={{ width: `${mpPct}%` }} />
                  <div className="m-vital-bar-text">{currentMp}/{character.max_mp}</div>
                </div>
              </div>
            </div>

            {/* XP Bar */}
            <div className="m-xp-area">
              <div className="m-char-name">
                Lv.{level} · {character.name} · {character.advanced_class || character.class}
              </div>
              <div className="m-xp-bar">
                <div className="m-xp-bar-fill" style={{ width: `${xpPct}%` }} />
                <div className="m-xp-bar-text">XP: {formatCurrency(xpIntoLevel)} / {formatCurrency(xpNeeded)}</div>
              </div>
            </div>

            {/* Quick Items */}
            <div className="m-quick-area">
              <div className="m-gold-display">💰 {formatCurrency(character.gold || 0)}{character.crowns > 0 && <span style={{marginLeft: '8px', color: '#ffd700'}}>👑 {formatCurrency(character.crowns)}</span>}{character.world_tier > 0 && <span onClick={() => setShowWorldTier(true)} style={{marginLeft: '8px', cursor: 'pointer', color: {1:'#4fc3f7',2:'#66bb6a',3:'#ffa726',4:'#ab47bc',5:'#ef5350'}[character.world_tier] || '#aaa'}} title="World Tier">{['','⚔️','🛡️','🏆','💎','🔥'][character.world_tier]} T{character.world_tier}</span>}</div>
              <div className="m-consumable-row">
                <div className="m-consumable-slot"><span className="m-slot-icon">-</span></div>
                <div className="m-consumable-slot"><span className="m-slot-icon">-</span></div>
                <div className="m-consumable-slot"><span className="m-slot-icon">-</span></div>
              </div>
              <div className="m-action-row">
                <button className="m-inventory-btn" onClick={() => setShowInventory(true)} title="Inventory">🎒</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Monster Encounter Card ── */}
        {spottedMonster && (
          <div className="m-encounter-overlay">
            <div className={`m-encounter-card${spottedMonster.monster.eliteTier ? ` encounter-${spottedMonster.monster.eliteTier}` : ''}`}>
              <img
                src={`/images/monsters/${spottedMonster.monster.pic}`}
                alt={spottedMonster.monster.name}
                onError={(e) => { e.target.src = '/images/monsters/default.jpg'; }}
                className="m-encounter-img"
              />
              <div className="m-encounter-info">
                <div className="m-encounter-name">⚔️ {spottedMonster.monster.name}</div>
                <div className="m-encounter-level">Level {spottedMonster.monster.level}</div>
                {spottedMonster.monster.eliteTier && (
                  <div className={`m-encounter-tier tier-${spottedMonster.monster.eliteTier}`}>
                    {spottedMonster.monster.eliteTier === 'legendary' ? '👑 LEGENDARY' : spottedMonster.monster.eliteTier === 'champion' ? '💀 CHAMPION' : '⚠️ ELITE'}
                  </div>
                )}
              </div>
              <button
                className="m-encounter-fight-btn"
                onClick={() => handleAttackMonster(spottedMonster.monster.name)}
                disabled={attackPending}
              >
                {attackPending ? '⏳' : '⚔️ Fight'}
              </button>
            </div>
          </div>
        )}

        {/* ── Gathering Node Overlay ── */}
        {gatherNode && !currentBattle && !currentConversation && !spottedMonster && (
          <div className="gather-overlay">
            <div className="gather-panel">
              <div className="gather-header">
                {gatherNode.type === 'mining' ? '⛏️ Mining Node' : '🌿 Herb Node'}
                <span className="gather-tier">({gatherNode.tier})</span>
              </div>
              {gatherResult ? (
                <div className="gather-result">
                  <div className="gather-result-title">Found:</div>
                  {gatherResult.materials.map((m, i) => (
                    <div key={i} className="gather-result-item">📦 {m.qty}x {m.name}</div>
                  ))}
                  <div className="gather-result-xp">+{gatherResult.xpGained} {gatherResult.discipline} XP</div>
                  <button className="gather-btn" onClick={handleGather} disabled={isGathering || gatherCooldown > 0}>
                    {isGathering ? '⏳ Gathering...' : gatherCooldown > 0 ? `⏳ Cooldown (${gatherCooldown}s)` : 'Gather Again'}
                  </button>
                </div>
              ) : (
                <div className="gather-prompt">
                  <div className="gather-prompt-text">
                    {gatherNode.type === 'mining'
                      ? 'You see mineral deposits in the rock face.'
                      : 'You spot useful herbs and plants growing here.'}
                  </div>
                  <button className="gather-btn" onClick={handleGather} disabled={isGathering || gatherCooldown > 0}>
                    {isGathering ? '⏳ Gathering...' : gatherCooldown > 0 ? `⏳ Cooldown (${gatherCooldown}s)` : gatherNode.type === 'mining' ? '⛏️ Mine' : '🌿 Gather'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Campfire Rest Result ── */}
        {campfireRestResult && !overworldEvent && (
          <div className="campfire-overlay">
            <div className="campfire-panel campfire-result">
              <div className="campfire-header">🔥 Campfire Rest</div>
              <div className="campfire-reward-list">
                <div className="campfire-reward-item campfire-reward-heal">❤️ Full HP Restored</div>
                <div className="campfire-reward-item campfire-reward-heal">💧 Full MP Restored</div>
                <div className="campfire-reward-item campfire-reward-xp">✨ +10% XP Bonus for 1 Hour</div>
              </div>
              <button className="campfire-rest-btn" onClick={() => setCampfireRestResult(false)}>Continue</button>
            </div>
          </div>
        )}

        {/* ── Campfire Rest Button ── */}
        {isNearCampfire && !campfireRestResult && !currentBattle && !currentConversation && !spottedMonster && !overworldEvent && (
          <div className="campfire-overlay">
            <div className="campfire-panel">
              <div className="campfire-header">🔥 Campfire</div>
              <div className="campfire-prompt">A warm campfire crackles nearby. Rest to restore your health and gain an experience bonus.</div>
              <button className="campfire-rest-btn" onClick={() => {
                if (socket) {
                  socket.emit('rest_at_campfire');
                  gameLogRef.current?.addLogEntry({ type: 'system', text: '🔥 You settle in by the campfire...' });
                }
              }}>🔥 Rest at Campfire</button>
            </div>
          </div>
        )}

        {/* ── Menu Drawer ── */}
        {showMobileMenu && (
          <>
            <div className="m-menu-backdrop" onClick={() => setShowMobileMenu(false)} />
            <div className="m-menu-drawer">
              <div className="m-menu-header">
                <div className="m-menu-char-name">{character.name}</div>
                <div className="m-menu-char-info">Lv.{level} {character.advanced_class || character.class}</div>
              </div>
              {/* Music controls intentionally omitted from the drawer — the
                  always-mounted .m-music-host handles playback so opening
                  this menu can't restart or duplicate the audio. */}
              {/* v642.10: Quick Settings inline at the top of the drawer for
                  mobile parity with the desktop ⚙️ popover. */}
              <div style={{ padding: '0 10px' }}>
                <QuickSettingsPopover
                  inline
                  onOpenFullSettings={() => { setShowMobileMenu(false); setShowSettings(true); }}
                />
              </div>
              <div className="m-menu-items">
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowCharacterSheet(true); }}>
                  <span className="m-menu-item-icon">⚔️</span>
                  <span className="m-menu-item-label">Character Sheet</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowInventory(true); }}>
                  <span className="m-menu-item-icon">🎒</span>
                  <span className="m-menu-item-label">Inventory</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowSkillTree(true); }}>
                  <span className="m-menu-item-icon">🌳</span>
                  <span className="m-menu-item-label">Skill Tree</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setDeckHubInitTab('library'); setShowDeckHub(true); }}>
                  <span className="m-menu-item-icon">🃏</span>
                  <span className="m-menu-item-label">Card Deck</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowSpellbook(true); }}>
                  <span className="m-menu-item-icon">📖</span>
                  <span className="m-menu-item-label">Spellbook</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowCompendium(true); }}>
                  <span className="m-menu-item-icon">📖</span>
                  <span className="m-menu-item-label">Compendium</span>
                  {gameState.questSummary && (gameState.questSummary.active > 0 || gameState.questSummary.available > 0) && (
                    <span className="m-menu-badge quest">{gameState.questSummary.active > 0 ? gameState.questSummary.active : '!'}</span>
                  )}
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); openProgressionGuide('overview'); }}>
                  <span className="m-menu-item-icon">📘</span>
                  <span className="m-menu-item-label">Progression Guide</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowSettings(true); }}>
                  <span className="m-menu-item-icon">⚙️</span>
                  <span className="m-menu-item-label">Settings</span>
                </button>
                <div className="m-menu-separator" />
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowMessages(true); }}>
                  <span className="m-menu-item-icon">✉️</span>
                  <span className="m-menu-item-label">Messages</span>
                  {unreadMessageCount > 0 && <span className="m-menu-badge">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>}
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowActiveBounties(true); }}>
                  <span className="m-menu-item-icon">📜</span>
                  <span className="m-menu-item-label">Active Bounties</span>
                  {bountyHudInfo?.claimable > 0 && <span className="m-menu-badge bounty-claim">{bountyHudInfo.claimable}</span>}
                  {!bountyHudInfo?.claimable && bountyHudInfo?.active > 0 && <span className="m-menu-badge bounty-active">{bountyHudInfo.active}</span>}
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowDailyDashboard(true); }}>
                  <span className="m-menu-item-icon">📅</span>
                  <span className="m-menu-item-label">Daily Dashboard</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowAchievements(true); }}>
                  <span className="m-menu-item-icon">🏆</span>
                  <span className="m-menu-item-label">Achievements</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowGuild(true); }}>
                  <span className="m-menu-item-icon">⚔️</span>
                  <span className="m-menu-item-label">Guild</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowFellowship(true); }}>
                  <span className="m-menu-item-icon">🤝</span>
                  <span className="m-menu-item-label">Fellowship</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowCrownShop(true); }}>
                  <span className="m-menu-item-icon">👑</span>
                  <span className="m-menu-item-label">Crown Shop</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowInfiniteQuest(true); }}>
                  <span className="m-menu-item-icon">📜</span>
                  <span className="m-menu-item-label">Endless Saga</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowWorldTier(true); }}>
                  <span className="m-menu-item-icon">🌍</span>
                  <span className="m-menu-item-label">World Tier</span>
                </button>
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowAtlas(true); }}>
                  <span className="m-menu-item-icon">🧭</span>
                  <span className="m-menu-item-label">Atlas</span>
                </button>
                {/* v642.7: Online entry for mobile parity with desktop */}
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowMobileOnline(true); }}>
                  <span className="m-menu-item-icon">👥</span>
                  <span className="m-menu-item-label">Online Players</span>
                  {(gameState.onlinePlayers?.length || 0) > 0 && (
                    <span className="m-menu-badge" style={{ background: '#2c7a3c' }}>{gameState.onlinePlayers.length}</span>
                  )}
                </button>
                <div className="m-menu-separator" />
                <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); setShowBugReport(true); }}>
                  <span className="m-menu-item-icon">🐛</span>
                  <span className="m-menu-item-label">Report Bug</span>
                </button>
                {user?.isAdmin && (
                  <button className="m-menu-item" onClick={() => { setShowMobileMenu(false); navigate('/admin'); }}>
                    <span className="m-menu-item-icon">🛠️</span>
                    <span className="m-menu-item-label">Admin Panel</span>
                  </button>
                )}
              </div>
              <div className="m-menu-footer">
                <button className="m-menu-footer-btn switch" onClick={() => { setShowMobileMenu(false); handleSwitchCharacter(); }}>🔄 Switch</button>
                <button className="m-menu-footer-btn logout" onClick={() => { setShowMobileMenu(false); handleLogout(); }}>Logout</button>
              </div>
            </div>
          </>
        )}

        {/* ── Chat Overlay ── */}
        {showMobileChat && (
          <div className="m-chat-overlay">
            <div className="m-chat-header">
              <h3>💬 Chat</h3>
              <button className="m-chat-close" onClick={() => setShowMobileChat(false)}>✕ Close</button>
            </div>
            <div className="m-chat-body">
              <ChatPanel
                chatMessages={gameState.chatMessages}
                onSendMessage={sendChatMessage}
                character={character}
                nearbyPlayers={gameState.nearbyPlayers}
                chatType={chatType}
                onChatTypeChange={setChatType}
                whisperTarget={whisperTarget}
                onClearWhisperTarget={() => setWhisperTarget(null)}
                fellowship={gameState.fellowship}
                socket={socket}
                onWhisper={(player) => setWhisperTarget(player)}
                onInspect={(id) => setInspectTargetId(id)}
                onReport={(target, recentMessages) => setReportTarget({ target, recentMessages })}
                onOpenCoC={() => setShowCoC(true)}
              />
            </div>
          </div>
        )}

        {/* ── v642.7: Online Players Overlay (mobile parity with desktop) ── */}
        {showMobileOnline && (
          <div className="m-chat-overlay">
            <div className="m-chat-header">
              <h3>👥 Online Players</h3>
              <button className="m-chat-close" onClick={() => setShowMobileOnline(false)}>✕ Close</button>
            </div>
            <div className="m-chat-body">
              <OnlinePlayersPanel
                onlinePlayers={gameState.onlinePlayers}
                character={character}
                onWhisper={(player) => { setWhisperTarget(player); setShowMobileOnline(false); setShowMobileChat(true); }}
                onSendMail={(player) => { setMailRecipient(player.name); setShowMobileOnline(false); setShowMessages(true); }}
                onInspect={(id) => { setShowMobileOnline(false); setInspectTargetId(id); }}
                onFellowshipInvite={gameState.fellowship ? (targetId) => { if (socket) socket.emit('fellowship_invite', { targetId }); } : null}
                onOpenGuildManage={() => { setShowMobileOnline(false); setShowGuild(true); }}
              />
            </div>
          </div>
        )}

        {/* ── v642.7: Persistent chat strip (Discord-mobile pattern) ── */}
        {!showMobileChat && !showMobileOnline && !currentBattle && (
          <MobileChatStrip
            chatMessages={gameState.chatMessages}
            onOpen={() => setShowMobileChat(true)}
            currentName={character?.name}
          />
        )}

        {/* ── Admin Broadcast Banner ── */}
        {adminBroadcast && !broadcastDismissed && (
          <div className="broadcast-banner" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 900 }}>
            <div className="broadcast-banner-content">
              <span className="broadcast-banner-icon">📢</span>
              <span className="broadcast-banner-message">{adminBroadcast.message}</span>
            </div>
            <button className="broadcast-banner-dismiss" onClick={() => setBroadcastDismissed(true)} title="Dismiss">✕</button>
          </div>
        )}

        {/* ══ Modals (shared with desktop) ══ */}
        {/* v642.1: Character Hub tab strip floats above whichever character panel is open */}
        <CharacterHubTabBar activeTab={characterHubTab} onSwitch={switchCharacterHubTab} />
        {/* v642.2: Adventure Hub tab strip */}
        <AdventureHubTabBar activeTab={adventureHubTab} onSwitch={switchAdventureHubTab} />
        {showCharacterSheet && (
          <CharacterSheet character={character} onClose={() => setShowCharacterSheet(false)} onCharacterUpdate={refreshCharacterData} onTeleport={handleTeleport} />
        )}
        {showInventory && (
          <InventoryPanel character={character} onClose={() => setShowInventory(false)} onCharacterUpdate={refreshCharacterData} onLogMessage={(msg) => gameLogRef.current?.addLogEntry({ type: 'action', text: msg })} onTeleport={handleTeleport} />
        )}
        {currentBattle && (
          <CombatInterface battle={currentBattle} onAction={handleCombatAction} onClose={handleCloseCombat} battleMusic={battleMusic} onTrack={trackAction} onCombatError={combatErrorRef.current} characterId={character?.id} />
        )}
        {battleResult && (
          <PostBattleScreen battleResult={battleResult} character={character} questSummary={gameState.questSummary} onClose={handleClosePostBattle} onCharacterUpdate={refreshCharacterData} onOpenGuide={openProgressionGuide} onOpenSpellbook={() => setShowSpellbook(true)} />
        )}

        {pendingLevelUp && !battleResult && !currentBattle && (
          <LevelUpModal character={character} onCharacterUpdate={refreshCharacterData} onClose={() => setPendingLevelUp(false)} />
        )}
        {pendingACSelection && !pendingLevelUp && !battleResult && !currentBattle && (
          <AdvancedClassSelection character={character} onCharacterUpdate={refreshCharacterData} onClose={() => setPendingACSelection(false)} />
        )}
        {inventoryOverflow && !battleResult && (
          <InventoryOverflowModal inventory={inventoryOverflow.inventory} maxCapacity={inventoryOverflow.maxCapacity} onResolved={() => refreshCharacterData()} />
        )}
        {warpChoice && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999
          }}>
            <div style={{
              background: 'var(--bg-panel, #2a2a2a)', border: '2px solid var(--border-gold, #b89d65)',
              borderRadius: '8px', padding: '1.5rem', maxWidth: '360px', width: '90%',
              textAlign: 'center', color: 'var(--text-primary, #ddd)',
              fontFamily: 'var(--font-ui, inherit)'
            }}>
              {warpChoice.warpType === 'waypoints' ? (<>
                <div style={{ fontSize: '1.1rem', marginBottom: '0.8rem', color: 'var(--text-gold, #d4af37)' }}>
                  🌐 Warp III — Waypoint Network
                </div>
                <div style={{ fontSize: '0.8rem', marginBottom: '0.8rem', color: '#aaa' }}>
                  {warpChoice.waypoints.length}/{warpChoice.maxWaypoints} waypoints set
                </div>
                {warpChoice.waypoints.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.8rem' }}>
                    {warpChoice.waypoints.map((wp, i) => (
                      <div key={i} style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                        <button
                          onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); handleCastSpell(spell, { action: 'warp_to_waypoint', slot: wp.slot }); }}
                          style={{
                            flex: 1, padding: '0.4rem 0.6rem', fontSize: '0.8rem', cursor: 'pointer',
                            background: '#2a5a3a', border: '1px solid #4a8a5a',
                            borderRadius: '4px', color: '#aaffaa', fontFamily: 'var(--font-ui, inherit)',
                            textAlign: 'left'
                          }}
                        >
                          🌀 {wp.name} <span style={{ color: '#8dc6ff', fontSize: '0.7rem' }}>({wp.label.split('(').slice(1).join('(')}</span>
                        </button>
                        <button
                          onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); setWaypointName(''); handleCastSpell(spell, { action: 'set_waypoint', slot: wp.slot, waypointName: waypointName || undefined }); }}
                          title="Overwrite this waypoint with current location"
                          style={{
                            padding: '0.4rem', fontSize: '0.7rem', cursor: 'pointer',
                            background: '#444', border: '1px solid #666', borderRadius: '4px', color: '#ddd'
                          }}
                        >📌</button>
                      </div>
                    ))}
                  </div>
                )}
                {warpChoice.waypoints.length < warpChoice.maxWaypoints && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
                    <input
                      type="text" placeholder="Waypoint name (optional)" maxLength={24}
                      value={waypointName} onChange={e => setWaypointName(e.target.value)}
                      style={{
                        padding: '0.4rem 0.6rem', fontSize: '0.8rem', background: '#1a1a1a',
                        border: '1px solid #555', borderRadius: '4px', color: '#ddd',
                        fontFamily: 'var(--font-ui, inherit)', textAlign: 'center'
                      }}
                    />
                    <button
                      onClick={() => { const spell = warpChoice.spellName; const name = waypointName; setWarpChoice(null); setWaypointName(''); handleCastSpell(spell, { action: 'set_waypoint', waypointName: name || undefined }); }}
                      style={{
                        padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer',
                        background: 'var(--bg-panel-alt, #444)', border: '1px solid var(--border-dark, #555)',
                        borderRadius: '4px', color: 'var(--text-primary, #ddd)',
                        fontFamily: 'var(--font-ui, inherit)'
                      }}
                    >
                      📌 Save current location as waypoint
                    </button>
                  </div>
                )}
                <button
                  onClick={() => { setWarpChoice(null); setWaypointName(''); }}
                  style={{
                    padding: '0.35rem 1rem', fontSize: '0.78rem', cursor: 'pointer',
                    background: 'transparent', border: '1px solid #555',
                    borderRadius: '4px', color: '#999',
                    fontFamily: 'var(--font-ui, inherit)', marginTop: '0.3rem'
                  }}
                >
                  Cancel
                </button>
              </>) : (<>
                <div style={{ fontSize: '1.1rem', marginBottom: '0.8rem', color: 'var(--text-gold, #d4af37)' }}>
                  ✨ Warp II
                </div>
                <div style={{ fontSize: '0.85rem', marginBottom: '1rem', lineHeight: '1.4' }}>
                  {warpChoice.hasExistingMark
                    ? <>Your mark is set at <strong style={{ color: '#8dc6ff' }}>{warpChoice.markLabel}</strong>.</>
                    : <>You have no marked location yet.</>
                  }
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button
                    onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); handleCastSpell(spell, { action: 'set_mark' }); }}
                    style={{
                      padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer',
                      background: 'var(--bg-panel-alt, #444)', border: '1px solid var(--border-dark, #555)',
                      borderRadius: '4px', color: 'var(--text-primary, #ddd)',
                      fontFamily: 'var(--font-ui, inherit)'
                    }}
                  >
                    📌 Set current location as mark
                  </button>
                  <button
                    onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); handleCastSpell(spell, { action: 'warp_to_mark' }); }}
                    disabled={!warpChoice.hasExistingMark}
                    style={{
                      padding: '0.5rem 1rem', fontSize: '0.85rem',
                      cursor: warpChoice.hasExistingMark ? 'pointer' : 'not-allowed',
                      background: warpChoice.hasExistingMark ? '#2a5a3a' : '#333',
                      border: '1px solid ' + (warpChoice.hasExistingMark ? '#4a8a5a' : '#444'),
                      borderRadius: '4px', color: warpChoice.hasExistingMark ? '#aaffaa' : '#666',
                      fontFamily: 'var(--font-ui, inherit)',
                      opacity: warpChoice.hasExistingMark ? 1 : 0.5
                    }}
                  >
                    🌀 Warp to mark{warpChoice.hasExistingMark ? ` (${warpChoice.markLabel})` : ''}
                  </button>
                  <button
                    onClick={() => setWarpChoice(null)}
                    style={{
                      padding: '0.35rem 1rem', fontSize: '0.78rem', cursor: 'pointer',
                      background: 'transparent', border: '1px solid #555',
                      borderRadius: '4px', color: '#999',
                      fontFamily: 'var(--font-ui, inherit)', marginTop: '0.3rem'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>)}
            </div>
          </div>
        )}
        {showShop && (
          <ShopModal character={character} onClose={() => setShowShop(false)} onCharacterUpdate={refreshCharacterData} />
        )}
        {showCrownShop && (
          <CrownShopModal character={character} onClose={() => setShowCrownShop(false)} onCharacterUpdate={refreshCharacterData} />
        )}
        {showWorldTier && (
          <WorldTierModal character={character} onClose={() => setShowWorldTier(false)} onTierChanged={() => refreshCharacterData()} onOpenGuide={openProgressionGuide} />
        )}
        {showAtlas && (
          <AtlasPanel character={character} onClose={() => setShowAtlas(false)} onOpenGuide={openProgressionGuide} />
        )}
        {showProgressionGuide && (
          <ProgressionGuidePanel onClose={() => setShowProgressionGuide(false)} initialSection={guideSection} />
        )}
        {showMessages && (
          <MessagesModal character={character} onClose={() => { setShowMessages(false); setMailRecipient(null); }} onUnreadUpdate={fetchUnreadCount} onCharacterUpdate={refreshCharacterData} initialComposeTo={mailRecipient} />
        )}
        {showMarket && (
          <MarketModal character={character} onClose={() => setShowMarket(false)} onCharacterUpdate={refreshCharacterData} />
        )}
        {showArena && (
          <ArenaModal character={character} socket={socket} onClose={() => { if (!inArenaBattle) setShowArena(false); }} />
        )}
        {showSkillTree && (
          <SkillTreePanel character={character} onClose={() => setShowSkillTree(false)} onCharacterUpdate={refreshCharacterData} />
        )}
        {showCrafting && (
          <CraftingPanel character={character} onClose={() => setShowCrafting(false)} onCharacterUpdate={refreshCharacterData} />
        )}
        {showStash && (
          <StashPanel character={character} onClose={() => setShowStash(false)} onCharacterUpdate={refreshCharacterData} />
        )}
        {showDeckHub && (
          <DeckManager character={character} initialTab={deckHubInitTab} onClose={() => setShowDeckHub(false)} />
        )}
        {showSpellbook && <SpellbookPanel onClose={() => setShowSpellbook(false)} />}
        {showCoC && <CodeOfConductModal onClose={() => setShowCoC(false)} />}
        {showBountyBoard && (
          <BountyBoardPanel character={character} area={character.current_area} onClose={() => setShowBountyBoard(false)} onCharacterUpdate={refreshCharacterData} onOpenGuide={openProgressionGuide} />
        )}
        {showDelve && (
          <DelveModal character={character} socket={socket} currentBattle={currentBattle} onBattleStart={() => setShowDelve(false)} onClose={() => setShowDelve(false)} onCharacterUpdate={refreshCharacterData} onOpenGuide={openProgressionGuide} />
        )}
        {overworldEvent && (
          <OverworldEventModal
            event={overworldEvent}
            result={overworldEventResult}
            nextStep={overworldNextStep}
            character={character}
            onChoose={async (eventId, optionId) => {
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/api/overworld-events/resolve`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ eventId, optionId }),
                });
                const data = await res.json();
                if (data.error) { gameLogRef.current?.addLogEntry({ type: 'error', text: data.error }); return; }
                setOverworldEventResult(data.resolution);
                setOverworldNextStep(data.nextStep || null);
                if (data.campfireRest) {
                  setCampfireRestResult(true);
                  gameLogRef.current?.addLogEntry({ type: 'system', text: '🔥 The campfire\'s warmth restores you to full health and mana.' });
                  gameLogRef.current?.addLogEntry({ type: 'system', text: '✨ You feel invigorated — +10% XP bonus for the next hour!' });
                }
                refreshCharacterData();
              } catch (err) { gameLogRef.current?.addLogEntry({ type: 'error', text: 'Failed to resolve event.' }); }
            }}
            onContinue={() => {
              if (overworldNextStep) {
                setOverworldEvent(prev => ({ ...prev, description: overworldNextStep.description, options: overworldNextStep.options, currentStep: overworldNextStep.currentStep }));
                setOverworldEventResult(null);
                setOverworldNextStep(null);
              }
            }}
            onDismiss={() => { setOverworldEvent(null); setOverworldEventResult(null); setOverworldNextStep(null); refreshCharacterData(); }}
          />
        )}
        {showActiveBounties && (
          <ActiveBountiesPanel onClose={() => setShowActiveBounties(false)} onOpenGuide={openProgressionGuide} />
        )}
        {showDailyDashboard && (
          <DailyDashboard
            onClose={() => setShowDailyDashboard(false)}
            onOpenBounties={() => { setShowDailyDashboard(false); setShowActiveBounties(true); }}
            onOpenDelve={() => { setShowDailyDashboard(false); setShowDelve(true); }}
            onOpenCompendium={() => { setShowDailyDashboard(false); setShowCompendium(true); }}
            onOpenCrownShop={() => { setShowDailyDashboard(false); setShowCrownShop(true); }}
          />
        )}
        {showAchievements && (
          <AchievementsModal onClose={() => setShowAchievements(false)} />
        )}
        {showBugReport && (
          <BugReportModal gameState={gameState} recentActions={recentActionsRef.current} currentBattle={currentBattle} onClose={() => setShowBugReport(false)} />
        )}
        {reportTarget && (
          <ReportPlayerModal
            target={reportTarget.target}
            recentChatMessages={reportTarget.recentMessages || []}
            onClose={() => setReportTarget(null)}
          />
        )}
        {showCompendium && (
          <Compendium
            character={character}
            onClose={() => setShowCompendium(false)}
            onOpenGuide={openProgressionGuide}
            initialQuestFilter={
              gameState?.questSummary?.active === 0 && gameState?.questSummary?.available > 0
                ? 'available'
                : 'all'
            }
            onOpenWorldMap={async () => {
              if (generatingWorldMap) return;
              setGeneratingWorldMap(true);
              try {
                const token = localStorage.getItem('token');
                const resp = await fetch(`${API_BASE_URL}/api/game/worldmap?size=8`, { headers: { Authorization: `Bearer ${token}` } });
                if (!resp.ok) throw new Error('Failed to generate map');
                const blob = await resp.blob();
                window.open(URL.createObjectURL(blob), '_blank');
              } catch (err) { console.error('World map error:', err); }
              finally { setGeneratingWorldMap(false); }
            }}
          />
        )}
        {showGuild && (
          <GuildPanel character={character} onClose={() => setShowGuild(false)} onCharacterUpdate={refreshCharacterData} />
        )}
        {inspectTargetId && (
          <InspectModal targetId={inspectTargetId} onClose={() => setInspectTargetId(null)} />
        )}
        {showFellowship && (
          <FellowshipPanel fellowship={gameState.fellowship} character={character} onlinePlayers={gameState.onlinePlayers} socket={socket} onInspect={(id) => setInspectTargetId(id)} onClose={() => setShowFellowship(false)} />
        )}
        {gameState.fellowshipInvite && (
          <div className="fellowship-invite-popup">
            <div className="fellowship-invite-content">
              <p>🤝 <strong>{gameState.fellowshipInvite.leaderName}</strong> invites you to join their fellowship!</p>
              <div className="fellowship-invite-btns">
                <button className="btn-primary" onClick={() => { if (socket) socket.emit('fellowship_accept', { fellowshipId: gameState.fellowshipInvite.fellowshipId }); dismissFellowshipInvite(); }}>Accept</button>
                <button className="btn-danger" onClick={() => { if (socket) socket.emit('fellowship_decline', { fellowshipId: gameState.fellowshipInvite.fellowshipId }); dismissFellowshipInvite(); }}>Decline</button>
              </div>
            </div>
          </div>
        )}
        {gameState.fellowshipBattle && !battleResult && (
          <FellowshipCombatInterface battleState={gameState.fellowshipBattle} character={character} socket={socket} chatMessages={gameState.chatMessages} onTrack={trackAction} />
        )}

        {/* Version Badge */}
        <div className="m-version-badge">{buildVersion}</div>

        {showSettings && <SettingsPanel
          onClose={() => setShowSettings(false)}
          onShowPatchNotes={() => {
            setShowSettings(false);
            setPatchNotesSinceVersion(null);
            setShowPatchNotes(true);
          }}
        />}
        {showPatchNotes && !showWelcome && (
          <PatchNotesModal
            sinceVersion={patchNotesSinceVersion}
            onClose={() => setShowPatchNotes(false)}
          />
        )}
        {isAFK && (
          <AFKModal
            secondsLeft={afkSecondsLeft}
            onStay={dismissAFK}
            onLogout={() => { dismissAFK(); logout(); navigate('/login'); }}
          />
        )}
        {!isConnected && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10500,
              background: 'linear-gradient(180deg, #6a4a1a, #4a3010)',
              color: '#ffd96b',
              padding: '6px 16px',
              borderRadius: '0 0 8px 8px',
              border: '1px solid #c9a84c',
              borderTop: 'none',
              fontSize: 13,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}
            title="Trying to restore your connection…"
          >
            📡 Reconnecting…
          </div>
        )}
      </div>
      </CharacterProvider>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // MODERN DESKTOP LAYOUT — Full-screen map with overlay HUD
  // ════════════════════════════════════════════════════════════════
  if (!isMobile && desktopLayout === 'modern') {
    const character = gameState.character;
    const xpForLvl = (lvl) => Math.ceil(((2 * lvl - 1) ** 2 - 1) / 8) * 1000;
    const level = characterLevel(character.experience);
    const xpThisLevel = xpForLvl(level);
    const xpNextLevel = xpForLvl(level + 1);
    const xpIntoLevel = character.experience - xpThisLevel;
    const xpNeeded = xpNextLevel - xpThisLevel;
    const xpPct = xpNeeded > 0 ? (xpIntoLevel / xpNeeded) * 100 : 100;
    const currentHp = character.max_hp - character.damage;
    const currentMp = character.max_mp - character.used_mp;
    const hpPct = character.max_hp > 0 ? (currentHp / character.max_hp) * 100 : 0;
    const mpPct = character.max_mp > 0 ? (currentMp / character.max_mp) * 100 : 0;
    const dmMenuBadgeCount = unreadMessageCount + (bountyHudInfo?.claimable || 0);

    return (
      <CharacterProvider character={character}>
      <div className="dm-game-container">
        {/* ── Full-Screen Map ── */}
        <div className="dm-map-fullscreen">
          <MapGrid
            character={character}
            maze={gameState.maze}
            nearbyPlayers={gameState.nearbyPlayers}
            onPlayerClick={(id) => setInspectTargetId(id)}
            viewCols={dmViewCols}
            viewRows={dmViewRows}
            gridLayout={true}
            showFullMaze={true}
            onMove={(dir) => {
              if (!currentBattle && !gameState.fellowshipBattle && !overworldEvent) {
                if (currentConversation) { setCurrentConversation(null); gameLogRef.current?.setPendingChoices(null); }
                setSpottedMonster(null);
                movePlayer(dir);
              }
            }}
          />
        </div>

        {/* ── HUD Overlay ── */}
        <div className="dm-hud">
          {/* ── Welcome Modal (first-time) ── */}
          {showWelcome && <WelcomeModal onClose={handleWelcomeClose} characterId={gameState.character?.id} />}
          {/* ── Contextual Hints ── */}
          <HintBanner character={character} inCombat={!!currentBattle} hasQuest={gameState.questSummary?.active > 0} hasLevelUp={pendingLevelUp} onOpenGuide={openProgressionGuide} />
          {/* ── Quest Tracker ── */}
          {!currentBattle && (
            <QuestTracker
              questSummary={gameState.questSummary}
              onClick={() => setShowCompendium(true)}
              style={{ position: 'absolute', top: 50, zIndex: 20 }}
            />
          )}
          {/* ── Top Bar ── */}
          <div className="dm-top-bar">
            <div className="dm-top-left">
              <div style={{ position: 'relative' }}>
                <button className="dm-menu-btn" onClick={() => setShowDesktopMenu(true)}>☰</button>
                {dmMenuBadgeCount > 0 && (
                  <span className="dm-menu-btn-badge">{dmMenuBadgeCount > 9 ? '9+' : dmMenuBadgeCount}</span>
                )}
              </div>
              <div className="dm-connection">
                <div className="dm-connection-dot" style={{ color: isConnected ? '#4ade80' : '#ef4444' }} />
                <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
              </div>
              <MusicPlayer musicFile={gameState.maze?.music} paused={!!currentBattle || !!battleResult || !!gameState.fellowshipBattle} />
            </div>

            <div className="dm-area-banner">
              🏰 {(character.current_area || 'Unknown').replace('.dat', '').replace(/(\d+)$/, ' $1').replace(/^\w/, c => c.toUpperCase())}
            </div>

            <div className="dm-top-right">
              {/* v642.3: Consolidated Character Hub button (replaces Inventory/Character/Skills/Deck) */}
              <button className="dm-nav-btn" onClick={() => switchCharacterHubTab('inventory')} title="Character (Inventory · Equipment · Skills · Deck)">
                <img src="/images/icons/nav-character.png" alt="Character" className="nav-icon" />
              </button>
              {/* v642.3: Consolidated Adventure Hub button (replaces Compendium/Guide/Bounties/Daily/Achievements/Atlas) */}
              <button className="dm-nav-btn" onClick={() => switchAdventureHubTab('quests')} title="Adventure (Quests · Atlas · Bounties · Daily · Trophies · Guide)" style={{ position: 'relative' }}>
                <img src="/images/icons/nav-compendium.png" alt="Adventure" className="nav-icon" />
                {gameState.questSummary && (gameState.questSummary.active > 0 || gameState.questSummary.available > 0) && (
                  <span className="dm-nav-badge quest">{gameState.questSummary.active > 0 ? gameState.questSummary.active : '!'}</span>
                )}
                {bountyHudInfo?.claimable > 0 && <span className="dm-nav-badge bounty-claim" style={{ right: -2, bottom: -2, top: 'auto' }}>{bountyHudInfo.claimable}</span>}
              </button>
              <div className="dm-nav-separator" />
              <button className="dm-nav-btn" onClick={async () => {
                if (generatingWorldMap) return;
                setGeneratingWorldMap(true);
                try {
                  const token = localStorage.getItem('token');
                  const resp = await fetch(`${API_BASE_URL}/api/game/worldmap?size=8`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!resp.ok) throw new Error('Failed');
                  const blob = await resp.blob();
                  window.open(URL.createObjectURL(blob), '_blank');
                } catch (err) { console.error('World map error:', err); }
                finally { setGeneratingWorldMap(false); }
              }} disabled={generatingWorldMap} title="World Map">
                {generatingWorldMap ? '⏳' : <img src="/images/icons/nav-world-map.png" alt="World Map" className="nav-icon" />}
              </button>
              <button className="dm-nav-btn" onClick={() => setShowMessages(true)} title="Messages" style={{ position: 'relative' }}>
                <img src="/images/icons/nav-messages.png" alt="Messages" className="nav-icon" />
                {unreadMessageCount > 0 && <span className={`dm-nav-badge${mailPulse ? ' mail-pulse' : ''}`}>{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>}
              </button>
              <NotificationCenter
                onOpenMessages={() => setShowMessages(true)}
                onOpenBounties={() => switchAdventureHubTab('bounties')}
                onOpenGuild={() => setShowGuild(true)}
              />
              <button className="dm-nav-btn" onClick={() => setShowWorldTier(true)} title="World Tier">🌍</button>
              <button className="dm-nav-btn" onClick={() => setShowInfiniteQuest(true)} title="The Endless Saga (Chronicler)">📜</button>
              <button className="dm-nav-btn" onClick={() => setShowCrownShop(true)} title="Crown Shop">👑</button>
              <div className="dm-nav-separator" />
              <button className="dm-nav-btn" onClick={() => setShowDesktopChat(p => !p)} title="Toggle Chat">💬</button>
              <button className="dm-nav-btn" onClick={() => setShowDesktopLog(p => !p)} title="Toggle Log">📋</button>
              <button className="dm-nav-btn" onClick={() => setShowDesktopOnline(p => !p)} title={`Online Players (${gameState.onlinePlayers?.length || 0} online now)`}>
                👥
                {(gameState.onlinePlayers?.length || 0) > 0 && (
                  <span className="dm-nav-badge" style={{ background: '#2c7a3c' }}>{gameState.onlinePlayers.length}</span>
                )}
              </button>
              {/* v642.8: ⚙️ opens a quick popover; "More settings…" link inside opens the full panel
                  v642.11: popover portals to body, anchored to this button via ref */}
              <div style={{ position: 'relative' }}>
                <button ref={quickSettingsAnchorRef} className="dm-nav-btn" onClick={() => setShowQuickSettings(p => !p)} title="Quick settings">⚙️</button>
                {showQuickSettings && (
                  <QuickSettingsPopover
                    anchorRef={quickSettingsAnchorRef}
                    onClose={() => setShowQuickSettings(false)}
                    onOpenFullSettings={() => setShowSettings(true)}
                  />
                )}
              </div>
              {user?.isAdmin && (
                <button className="dm-nav-btn" onClick={() => navigate('/admin')} title="Admin" style={{ borderColor: 'rgba(231,76,60,0.4)', color: '#e07070' }}>🛠️</button>
              )}
            </div>
          </div>

          {/* ── Treasure Map HUD ── */}
          {character.treasure_map && (() => {
            const tmRarity = character.treasure_map.rarity || 'common';
            const tmRarityLabel = { common: '', rare: 'Rare ', legendary: 'Legendary ' }[tmRarity] || '';
            return (
              <div className="dm-treasure-hud">
                <div className={`treasure-map-hud treasure-hud-${tmRarity}`}>
                  <span className="treasure-map-hud-icon">🗺️</span>
                  <span className="treasure-map-hud-text">
                    {tmRarityLabel && <strong>{tmRarityLabel}</strong>}
                    {character.treasure_map.area === character.current_area
                      ? 'Treasure nearby! Follow the map icon.'
                      : `Map leads to ${(character.treasure_map.area || '').replace(/\.dat$/, '').replace(/\d+$/, '')}`}
                  </span>
                  <button className="treasure-map-hud-discard" title="Discard" onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      await fetch(`${API_BASE_URL}/api/treasure-map/discard`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                      gameLogRef.current?.addLogEntry({ type: 'system', text: '🗺️ Treasure map discarded.' });
                      setGameState(prev => ({
                        ...prev,
                        character: prev.character ? { ...prev.character, treasure_map: null } : prev.character,
                        maze: prev.maze ? { ...prev.maze, treasureMapTile: null } : prev.maze,
                      }));
                      refreshCharacterData();
                    } catch (err) {
                      gameLogRef.current?.addLogEntry({ type: 'error', text: 'Failed to discard map.' });
                    }
                  }}>✕</button>
                </div>
              </div>
            );
          })()}

          {/* ── Campfire XP Buff HUD ── */}
          {(() => {
            let activeBuffs = character.active_buffs;
            try { activeBuffs = typeof activeBuffs === 'string' ? JSON.parse(activeBuffs) : (activeBuffs || []); } catch(e) { activeBuffs = []; }
            const cfBuff = activeBuffs.find(b => b.type === 'campfire_xp' && b.expiresAt && Date.now() < b.expiresAt);
            if (!cfBuff) return null;
            const remaining = cfBuff.expiresAt - Date.now();
            const mins = Math.ceil(remaining / 60000);
            return (
              <div className="dm-buff-hud">
                <div className="campfire-buff-hud">
                  <span className="campfire-buff-icon">🔥</span>
                  <span className="campfire-buff-text">+{cfBuff.value || 10}% XP ({mins}m)</span>
                </div>
              </div>
            );
          })()}

          {/* ── Monster Encounter Card ── */}
          {spottedMonster && (
            <div className="dm-encounter-card-wrapper">
              <div className="dm-encounter-card">
                <img
                  src={`/images/monsters/${spottedMonster.monster.pic}`}
                  alt={spottedMonster.monster.name}
                  onError={(e) => { e.target.src = '/images/monsters/default.jpg'; }}
                  className="dm-encounter-img"
                />
                <div className="dm-encounter-info">
                  <div className="dm-encounter-name">⚔️ {spottedMonster.monster.name}</div>
                  <div className="dm-encounter-level">Level {spottedMonster.monster.level}</div>
                </div>
                <button
                  className="dm-encounter-fight-btn"
                  onClick={() => handleAttackMonster(spottedMonster.monster.name)}
                  disabled={attackPending}
                >
                  {attackPending ? '⏳ Starting...' : '⚔️ Fight'}
                </button>
              </div>
            </div>
          )}

          {/* ── Bottom HUD ── */}
          <div className="dm-bottom-hud">
            <div className="dm-portrait-area">
              <div className="dm-portrait-frame" onClick={() => setShowCharacterSheet(true)}>
                {resolveCharacterPortrait(character) ? (
                  <img src={resolveCharacterPortrait(character)} alt="" className="dm-portrait-img" />
                ) : (
                  <span className="dm-portrait-placeholder">🧑</span>
                )}
              </div>
              <div className="dm-vital-column">
                <div className="dm-vital-bar">
                  <div className="dm-vital-bar-fill hp" style={{ width: `${hpPct}%` }} />
                  <div className="dm-vital-bar-text">{currentHp}/{character.max_hp} HP</div>
                </div>
                <div className="dm-vital-bar">
                  <div className="dm-vital-bar-fill mp" style={{ width: `${mpPct}%` }} />
                  <div className="dm-vital-bar-text">{currentMp}/{character.max_mp} MP</div>
                </div>
              </div>
            </div>

            <div className="dm-xp-area">
              <div className="dm-char-name">
                Lv.{level} · {character.name} · {character.advanced_class || character.class}
              </div>
              <div className="dm-xp-bar">
                <div className="dm-xp-bar-fill" style={{ width: `${xpPct}%` }} />
                <div className="dm-xp-bar-text">XP: {formatCurrency(xpIntoLevel)} / {formatCurrency(xpNeeded)}</div>
              </div>
            </div>

            <div className="dm-quick-area">
              <div className="dm-gold-display">💰 {formatCurrency(character.gold || 0)}{character.crowns > 0 && <span style={{marginLeft: '8px', color: '#ffd700'}}>👑 {formatCurrency(character.crowns)}</span>}{character.world_tier > 0 && <span onClick={() => setShowWorldTier(true)} style={{marginLeft: '8px', cursor: 'pointer', color: {1:'#4fc3f7',2:'#66bb6a',3:'#ffa726',4:'#ab47bc',5:'#ef5350'}[character.world_tier] || '#aaa'}} title="World Tier">{['','⚔️','🛡️','🏆','💎','🔥'][character.world_tier]} T{character.world_tier}</span>}</div>
              <div className="dm-action-row">
                {/* v642.4: Bottom HUD cleanup. Inventory removed (now via Character hub in top bar);
                    Guild + Fellowship + Online consolidated into a single Social dropdown. */}
                <SocialDropdown
                  onOpenGuild={() => setShowGuild(true)}
                  onOpenFellowship={() => setShowFellowship(true)}
                  onToggleOnline={() => setShowDesktopOnline(p => !p)}
                  onlineCount={gameState.onlinePlayers?.length || 0}
                />
              </div>
            </div>
          </div>

          {/* ── Hotkey Hints ── */}
          <div className="dm-hotkey-bar">
            <span className="dm-hotkey"><kbd>WASD</kbd> Move</span>
            <span className="dm-hotkey"><kbd>I</kbd> Inventory</span>
            <span className="dm-hotkey"><kbd>C</kbd> Character</span>
            <span className="dm-hotkey"><kbd>T</kbd> Skills</span>
            <span className="dm-hotkey"><kbd>J</kbd> Compendium</span>
            <span className="dm-hotkey"><kbd>G</kbd> Guide</span>
            <span className="dm-hotkey"><kbd>B</kbd> Shop</span>
            <span className="dm-hotkey"><kbd>M</kbd> Market</span>
            <span className="dm-hotkey"><kbd>N</kbd> Arena</span>
            <span className="dm-hotkey"><kbd>Esc</kbd> Close</span>
          </div>
        </div>

        {/* ── Floating Online Players Panel (v642.13: portalled to <body>
             so it can't be trapped under sibling stacking contexts like
             the game log or chat panel) ── */}
        {createPortal(
          <div className={`dm-online-panel ${showDesktopOnline ? '' : 'hidden'}`}>
            <OnlinePlayersPanel
              onlinePlayers={gameState.onlinePlayers}
              character={character}
              onWhisper={(player) => setWhisperTarget(player)}
              onSendMail={(player) => { setMailRecipient(player.name); setShowMessages(true); }}
              onInspect={(id) => setInspectTargetId(id)}
              onFellowshipInvite={gameState.fellowship ? (targetId) => { if (socket) socket.emit('fellowship_invite', { targetId }); } : null}
              onOpenGuildManage={() => setShowGuild(true)}
            />
          </div>,
          document.body
        )}

        {/* ── Floating Game Log ── */}
        <button className={`dm-log-toggle ${showDesktopLog ? '' : 'collapsed'}`} onClick={() => setShowDesktopLog(p => !p)}>
          {showDesktopLog ? '▶' : '◀'}
        </button>
        <div className={`dm-log-panel ${showDesktopLog ? '' : 'collapsed'} ${showDesktopChat ? 'chat-open' : ''}`}>
          <GameLog
            ref={gameLogRef}
            character={character}
            onConversationChoice={handleConversationChoice}
            onConversationClose={closeConversation}
          />
        </div>

        {/* ── Floating Chat ── */}
        <button className={`dm-chat-toggle ${showDesktopChat ? '' : 'collapsed'}`} onClick={() => setShowDesktopChat(p => !p)} title="Toggle Chat">
          {showDesktopChat ? '▼' : '▲'}
        </button>
        <div className={`dm-chat-panel ${showDesktopChat ? '' : 'hidden'}`}>
          <ChatPanel
            chatMessages={gameState.chatMessages}
            onSendMessage={sendChatMessage}
            character={character}
            nearbyPlayers={gameState.nearbyPlayers}
            chatType={chatType}
            onChatTypeChange={setChatType}
            whisperTarget={whisperTarget}
            onClearWhisperTarget={() => setWhisperTarget(null)}
            fellowship={gameState.fellowship}
            socket={socket}
            onWhisper={(player) => setWhisperTarget(player)}
            onInspect={(id) => setInspectTargetId(id)}
            onReport={(target, recentMessages) => setReportTarget({ target, recentMessages })}
            onOpenCoC={() => setShowCoC(true)}
          />
        </div>

        {/* ── Drawer Menu ── */}
        {showDesktopMenu && (
          <>
            <div className="dm-drawer-backdrop" onClick={() => setShowDesktopMenu(false)} />
            <div className="dm-drawer">
              <div className="dm-drawer-header">
                <div className="dm-drawer-char-name">{character.name}</div>
                <div className="dm-drawer-char-info">Lv.{level} {character.advanced_class || character.class}</div>
              </div>
              {/* Music controls intentionally omitted — the always-on top
                  bar already hosts the MusicPlayer; mounting a second one
                  here was double-playing the track. */}
              <div className="dm-drawer-items">
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowCharacterSheet(true); }}>
                  <span className="dm-drawer-item-icon">⚔️</span><span className="dm-drawer-item-label">Character Sheet</span>
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowInventory(true); }}>
                  <span className="dm-drawer-item-icon">🎒</span><span className="dm-drawer-item-label">Inventory</span>
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowSkillTree(true); }}>
                  <span className="dm-drawer-item-icon">🌳</span><span className="dm-drawer-item-label">Skill Tree</span>
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setDeckHubInitTab('library'); setShowDeckHub(true); }}>
                  <span className="dm-drawer-item-icon">🃏</span><span className="dm-drawer-item-label">Card Deck</span>
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowSpellbook(true); }}>
                  <span className="dm-drawer-item-icon">📖</span><span className="dm-drawer-item-label">Spellbook</span>
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowCompendium(true); }}>
                  <span className="dm-drawer-item-icon">📖</span><span className="dm-drawer-item-label">Compendium</span>
                  {gameState.questSummary && (gameState.questSummary.active > 0 || gameState.questSummary.available > 0) && (
                    <span className="dm-drawer-badge quest">{gameState.questSummary.active > 0 ? gameState.questSummary.active : '!'}</span>
                  )}
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); openProgressionGuide('overview'); }}>
                  <span className="dm-drawer-item-icon">📘</span><span className="dm-drawer-item-label">Progression Guide</span>
                </button>
                <div className="dm-drawer-separator" />
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowMessages(true); }}>
                  <span className="dm-drawer-item-icon">✉️</span><span className="dm-drawer-item-label">Messages</span>
                  {unreadMessageCount > 0 && <span className="dm-drawer-badge">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>}
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowActiveBounties(true); }}>
                  <span className="dm-drawer-item-icon">📜</span><span className="dm-drawer-item-label">Active Bounties</span>
                  {bountyHudInfo?.claimable > 0 && <span className="dm-drawer-badge bounty-claim">{bountyHudInfo.claimable}</span>}
                  {!bountyHudInfo?.claimable && bountyHudInfo?.active > 0 && <span className="dm-drawer-badge bounty-active">{bountyHudInfo.active}</span>}
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowGuild(true); }}>
                  <span className="dm-drawer-item-icon">⚔️</span><span className="dm-drawer-item-label">Guild</span>
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowFellowship(true); }}>
                  <span className="dm-drawer-item-icon">🤝</span><span className="dm-drawer-item-label">Fellowship</span>
                </button>
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowAtlas(true); }}>
                  <span className="dm-drawer-item-icon">🧭</span><span className="dm-drawer-item-label">Atlas</span>
                </button>
                <div className="dm-drawer-separator" />
                <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); setShowBugReport(true); }}>
                  <span className="dm-drawer-item-icon">🐛</span><span className="dm-drawer-item-label">Report Bug</span>
                </button>
                {user?.isAdmin && (
                  <button className="dm-drawer-item" onClick={() => { setShowDesktopMenu(false); navigate('/admin'); }}>
                    <span className="dm-drawer-item-icon">🛠️</span><span className="dm-drawer-item-label">Admin Panel</span>
                  </button>
                )}
              </div>
              <div className="dm-drawer-footer">
                <button className="dm-drawer-footer-btn ui-toggle" onClick={() => { setShowDesktopMenu(false); setDesktopLayout('classic'); localStorage.setItem('loria_desktop_layout', 'classic'); }}>
                  🖥️ Classic UI
                </button>
                <button className="dm-drawer-footer-btn switch" onClick={() => { setShowDesktopMenu(false); handleSwitchCharacter(); }}>
                  🔄 Switch
                </button>
                <button className="dm-drawer-footer-btn logout" onClick={() => { setShowDesktopMenu(false); handleLogout(); }}>
                  Logout
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Admin Broadcast Banner ── */}
        {adminBroadcast && !broadcastDismissed && (
          <div className="broadcast-banner" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 900 }}>
            <div className="broadcast-banner-content">
              <span className="broadcast-banner-icon">📢</span>
              <span className="broadcast-banner-message">{adminBroadcast.message}</span>
            </div>
            <button className="broadcast-banner-dismiss" onClick={() => setBroadcastDismissed(true)} title="Dismiss">✕</button>
          </div>
        )}

        {/* ── Campfire Rest Result ── */}
        {campfireRestResult && !overworldEvent && (
          <div className="campfire-overlay">
            <div className="campfire-panel campfire-result">
              <div className="campfire-header">🔥 Campfire Rest</div>
              <div className="campfire-reward-list">
                <div className="campfire-reward-item campfire-reward-heal">❤️ Full HP Restored</div>
                <div className="campfire-reward-item campfire-reward-heal">💧 Full MP Restored</div>
                <div className="campfire-reward-item campfire-reward-xp">✨ +10% XP Bonus for 1 Hour</div>
              </div>
              <button className="campfire-rest-btn" onClick={() => setCampfireRestResult(false)}>Continue</button>
            </div>
          </div>
        )}

        {/* ── Campfire Rest Button ── */}
        {isNearCampfire && !campfireRestResult && !currentBattle && !currentConversation && !spottedMonster && !overworldEvent && (
          <div className="campfire-overlay">
            <div className="campfire-panel">
              <div className="campfire-header">🔥 Campfire</div>
              <div className="campfire-prompt">A warm campfire crackles nearby. Rest to restore your health and gain an experience bonus.</div>
              <button className="campfire-rest-btn" onClick={() => {
                if (socket) {
                  socket.emit('rest_at_campfire');
                  gameLogRef.current?.addLogEntry({ type: 'system', text: '🔥 You settle in by the campfire...' });
                }
              }}>🔥 Rest at Campfire</button>
            </div>
          </div>
        )}

        {/* ══ Modals (shared) ══ */}
        {/* v642.1: Character Hub tab strip */}
        <CharacterHubTabBar activeTab={characterHubTab} onSwitch={switchCharacterHubTab} />
        {/* v642.2: Adventure Hub tab strip */}
        <AdventureHubTabBar activeTab={adventureHubTab} onSwitch={switchAdventureHubTab} />
        {showCharacterSheet && (
          <CharacterSheet character={character} onClose={() => setShowCharacterSheet(false)} onCharacterUpdate={refreshCharacterData} onTeleport={handleTeleport} />
        )}
        {showInventory && (
          <InventoryPanel character={character} onClose={() => setShowInventory(false)} onCharacterUpdate={refreshCharacterData} onLogMessage={(msg) => gameLogRef.current?.addLogEntry({ type: 'action', text: msg })} onTeleport={handleTeleport} />
        )}
        {currentBattle && (
          <CombatInterface battle={currentBattle} onAction={handleCombatAction} onClose={handleCloseCombat} battleMusic={battleMusic} onTrack={trackAction} onCombatError={combatErrorRef.current} modernLayout={desktopLayout === 'modern'} characterPortrait={CHARACTER_PORTRAITS[character?.name]} characterId={character?.id} />
        )}
        {battleResult && (
          <PostBattleScreen battleResult={battleResult} character={character} questSummary={gameState.questSummary} onClose={handleClosePostBattle} onCharacterUpdate={refreshCharacterData} onOpenGuide={openProgressionGuide} onOpenSpellbook={() => setShowSpellbook(true)} />
        )}
        {pendingLevelUp && !battleResult && !currentBattle && (
          <LevelUpModal character={character} onCharacterUpdate={refreshCharacterData} onClose={() => setPendingLevelUp(false)} />
        )}
        {pendingACSelection && !pendingLevelUp && !battleResult && !currentBattle && (
          <AdvancedClassSelection character={character} onCharacterUpdate={refreshCharacterData} onClose={() => setPendingACSelection(false)} />
        )}
        {inventoryOverflow && !battleResult && (
          <InventoryOverflowModal inventory={inventoryOverflow.inventory} maxCapacity={inventoryOverflow.maxCapacity} onResolved={() => refreshCharacterData()} />
        )}
        {warpChoice && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: 'var(--bg-panel, #2a2a2a)', border: '2px solid var(--border-gold, #b89d65)', borderRadius: '8px', padding: '1.5rem', maxWidth: '360px', width: '90%', textAlign: 'center', color: 'var(--text-primary, #ddd)', fontFamily: 'var(--font-ui, inherit)' }}>
              {warpChoice.warpType === 'waypoints' ? (<>
                <div style={{ fontSize: '1.1rem', marginBottom: '0.8rem', color: 'var(--text-gold, #d4af37)' }}>🌐 Warp III — Waypoint Network</div>
                <div style={{ fontSize: '0.8rem', marginBottom: '0.8rem', color: '#aaa' }}>{warpChoice.waypoints.length}/{warpChoice.maxWaypoints} waypoints set</div>
                {warpChoice.waypoints.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.8rem' }}>
                    {warpChoice.waypoints.map((wp, i) => (
                      <div key={i} style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                        <button onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); handleCastSpell(spell, { action: 'warp_to_waypoint', slot: wp.slot }); }} style={{ flex: 1, padding: '0.4rem 0.6rem', fontSize: '0.8rem', cursor: 'pointer', background: '#2a5a3a', border: '1px solid #4a8a5a', borderRadius: '4px', color: '#aaffaa', fontFamily: 'var(--font-ui, inherit)', textAlign: 'left' }}>
                          🌀 {wp.name} <span style={{ color: '#8dc6ff', fontSize: '0.7rem' }}>({wp.label.split('(').slice(1).join('(')}</span>
                        </button>
                        <button onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); setWaypointName(''); handleCastSpell(spell, { action: 'set_waypoint', slot: wp.slot, waypointName: waypointName || undefined }); }} title="Overwrite" style={{ padding: '0.4rem', fontSize: '0.7rem', cursor: 'pointer', background: '#444', border: '1px solid #666', borderRadius: '4px', color: '#ddd' }}>📌</button>
                      </div>
                    ))}
                  </div>
                )}
                {warpChoice.waypoints.length < warpChoice.maxWaypoints && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
                    <input type="text" placeholder="Waypoint name (optional)" maxLength={24} value={waypointName} onChange={e => setWaypointName(e.target.value)} style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', background: '#1a1a1a', border: '1px solid #555', borderRadius: '4px', color: '#ddd', fontFamily: 'var(--font-ui, inherit)', textAlign: 'center' }} />
                    <button onClick={() => { const spell = warpChoice.spellName; const name = waypointName; setWarpChoice(null); setWaypointName(''); handleCastSpell(spell, { action: 'set_waypoint', waypointName: name || undefined }); }} style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', background: 'var(--bg-panel-alt, #444)', border: '1px solid var(--border-dark, #555)', borderRadius: '4px', color: 'var(--text-primary, #ddd)', fontFamily: 'var(--font-ui, inherit)' }}>📌 Save current location as waypoint</button>
                  </div>
                )}
                <button onClick={() => { setWarpChoice(null); setWaypointName(''); }} style={{ padding: '0.35rem 1rem', fontSize: '0.78rem', cursor: 'pointer', background: 'transparent', border: '1px solid #555', borderRadius: '4px', color: '#999', fontFamily: 'var(--font-ui, inherit)', marginTop: '0.3rem' }}>Cancel</button>
              </>) : (<>
                <div style={{ fontSize: '1.1rem', marginBottom: '0.8rem', color: 'var(--text-gold, #d4af37)' }}>✨ Warp II</div>
                <div style={{ fontSize: '0.85rem', marginBottom: '1rem', lineHeight: '1.4' }}>
                  {warpChoice.hasExistingMark ? <>Your mark is set at <strong style={{ color: '#8dc6ff' }}>{warpChoice.markLabel}</strong>.</> : <>You have no marked location yet.</>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); handleCastSpell(spell, { action: 'set_mark' }); }} style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', background: 'var(--bg-panel-alt, #444)', border: '1px solid var(--border-dark, #555)', borderRadius: '4px', color: 'var(--text-primary, #ddd)', fontFamily: 'var(--font-ui, inherit)' }}>📌 Set current location as mark</button>
                  <button onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); handleCastSpell(spell, { action: 'warp_to_mark' }); }} disabled={!warpChoice.hasExistingMark} style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: warpChoice.hasExistingMark ? 'pointer' : 'not-allowed', background: warpChoice.hasExistingMark ? '#2a5a3a' : '#333', border: '1px solid ' + (warpChoice.hasExistingMark ? '#4a8a5a' : '#444'), borderRadius: '4px', color: warpChoice.hasExistingMark ? '#aaffaa' : '#666', fontFamily: 'var(--font-ui, inherit)', opacity: warpChoice.hasExistingMark ? 1 : 0.5 }}>🌀 Warp to mark{warpChoice.hasExistingMark ? ` (${warpChoice.markLabel})` : ''}</button>
                  <button onClick={() => setWarpChoice(null)} style={{ padding: '0.35rem 1rem', fontSize: '0.78rem', cursor: 'pointer', background: 'transparent', border: '1px solid #555', borderRadius: '4px', color: '#999', fontFamily: 'var(--font-ui, inherit)', marginTop: '0.3rem' }}>Cancel</button>
                </div>
              </>)}
            </div>
          </div>
        )}
        {showShop && <ShopModal character={character} onClose={() => setShowShop(false)} onCharacterUpdate={refreshCharacterData} />}
        {showCrownShop && <CrownShopModal character={character} onClose={() => setShowCrownShop(false)} onCharacterUpdate={refreshCharacterData} />}
        {showInfiniteQuest && <InfiniteQuestPanel character={character} onClose={() => setShowInfiniteQuest(false)} onCharacterUpdate={refreshCharacterData} onOpenCrownShop={() => setShowCrownShop(true)} />}
        {showWorldTier && <WorldTierModal character={character} onClose={() => setShowWorldTier(false)} onTierChanged={() => refreshCharacterData()} onOpenGuide={openProgressionGuide} />}
        {showAtlas && <AtlasPanel character={character} onClose={() => setShowAtlas(false)} onOpenGuide={openProgressionGuide} />}
        {showProgressionGuide && <ProgressionGuidePanel onClose={() => setShowProgressionGuide(false)} initialSection={guideSection} />}
        {showMessages && <MessagesModal character={character} onClose={() => { setShowMessages(false); setMailRecipient(null); }} onUnreadUpdate={fetchUnreadCount} onCharacterUpdate={refreshCharacterData} initialComposeTo={mailRecipient} />}
        {showMarket && <MarketModal character={character} onClose={() => setShowMarket(false)} onCharacterUpdate={refreshCharacterData} />}
        {showArena && <ArenaModal character={character} socket={socket} onClose={() => { if (!inArenaBattle) setShowArena(false); }} />}
        {showSkillTree && <SkillTreePanel character={character} onClose={() => setShowSkillTree(false)} onCharacterUpdate={refreshCharacterData} />}
        {showCrafting && <CraftingPanel character={character} onClose={() => setShowCrafting(false)} onCharacterUpdate={refreshCharacterData} />}
        {showStash && <StashPanel character={character} onClose={() => setShowStash(false)} onCharacterUpdate={refreshCharacterData} />}
        {showDeckHub && <DeckManager character={character} initialTab={deckHubInitTab} onClose={() => setShowDeckHub(false)} />}
        {showSpellbook && <SpellbookPanel onClose={() => setShowSpellbook(false)} />}
        {showCoC && <CodeOfConductModal onClose={() => setShowCoC(false)} />}
        {showBountyBoard && <BountyBoardPanel character={character} area={character.current_area} onClose={() => setShowBountyBoard(false)} onCharacterUpdate={refreshCharacterData} onOpenGuide={openProgressionGuide} />}
        {showDelve && <DelveModal character={character} socket={socket} currentBattle={currentBattle} onBattleStart={() => setShowDelve(false)} onClose={() => setShowDelve(false)} onCharacterUpdate={refreshCharacterData} onOpenGuide={openProgressionGuide} />}
        {overworldEvent && (
          <OverworldEventModal event={overworldEvent} result={overworldEventResult} nextStep={overworldNextStep} character={character}
            onChoose={async (eventId, optionId) => {
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/api/overworld-events/resolve`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ eventId, optionId }) });
                const data = await res.json();
                if (data.error) { gameLogRef.current?.addLogEntry({ type: 'error', text: data.error }); return; }
                setOverworldEventResult(data.resolution);
                setOverworldNextStep(data.nextStep || null);
                if (data.campfireRest) {
                  setCampfireRestResult(true);
                  gameLogRef.current?.addLogEntry({ type: 'system', text: '🔥 The campfire\'s warmth restores you to full health and mana.' });
                  gameLogRef.current?.addLogEntry({ type: 'system', text: '✨ You feel invigorated — +10% XP bonus for the next hour!' });
                }
                refreshCharacterData();
              } catch (err) { gameLogRef.current?.addLogEntry({ type: 'error', text: 'Failed to resolve event.' }); }
            }}
            onContinue={() => {
              if (overworldNextStep) {
                setOverworldEvent(prev => ({ ...prev, description: overworldNextStep.description, options: overworldNextStep.options, currentStep: overworldNextStep.currentStep }));
                setOverworldEventResult(null); setOverworldNextStep(null);
              }
            }}
            onDismiss={() => { setOverworldEvent(null); setOverworldEventResult(null); setOverworldNextStep(null); refreshCharacterData(); }}
          />
        )}
        {showActiveBounties && <ActiveBountiesPanel onClose={() => setShowActiveBounties(false)} onOpenGuide={openProgressionGuide} />}
        {showBugReport && <BugReportModal gameState={gameState} recentActions={recentActionsRef.current} currentBattle={currentBattle} onClose={() => setShowBugReport(false)} />}
        {reportTarget && (
          <ReportPlayerModal
            target={reportTarget.target}
            recentChatMessages={reportTarget.recentMessages || []}
            onClose={() => setReportTarget(null)}
          />
        )}
        {showCompendium && (
          <Compendium character={character} onClose={() => setShowCompendium(false)} onOpenGuide={openProgressionGuide}
            initialQuestFilter={
              gameState?.questSummary?.active === 0 && gameState?.questSummary?.available > 0
                ? 'available'
                : 'all'
            }
            onOpenWorldMap={async () => {
              if (generatingWorldMap) return;
              setGeneratingWorldMap(true);
              try {
                const token = localStorage.getItem('token');
                const resp = await fetch(`${API_BASE_URL}/api/game/worldmap?size=8`, { headers: { Authorization: `Bearer ${token}` } });
                if (!resp.ok) throw new Error('Failed');
                const blob = await resp.blob();
                window.open(URL.createObjectURL(blob), '_blank');
              } catch (err) { console.error('World map error:', err); }
              finally { setGeneratingWorldMap(false); }
            }}
          />
        )}
        {showGuild && <GuildPanel character={character} onClose={() => setShowGuild(false)} onCharacterUpdate={refreshCharacterData} />}
        {inspectTargetId && <InspectModal targetId={inspectTargetId} onClose={() => setInspectTargetId(null)} />}
        {showFellowship && <FellowshipPanel fellowship={gameState.fellowship} character={character} onlinePlayers={gameState.onlinePlayers} socket={socket} onInspect={(id) => setInspectTargetId(id)} onClose={() => setShowFellowship(false)} />}
        {gameState.fellowshipInvite && (
          <div className="fellowship-invite-popup">
            <div className="fellowship-invite-content">
              <p>🤝 <strong>{gameState.fellowshipInvite.leaderName}</strong> invites you to join their fellowship!</p>
              <div className="fellowship-invite-btns">
                <button className="btn-primary" onClick={() => { if (socket) socket.emit('fellowship_accept', { fellowshipId: gameState.fellowshipInvite.fellowshipId }); dismissFellowshipInvite(); }}>Accept</button>
                <button className="btn-danger" onClick={() => { if (socket) socket.emit('fellowship_decline', { fellowshipId: gameState.fellowshipInvite.fellowshipId }); dismissFellowshipInvite(); }}>Decline</button>
              </div>
            </div>
          </div>
        )}
        {gameState.fellowshipBattle && !battleResult && (
          <FellowshipCombatInterface battleState={gameState.fellowshipBattle} character={character} socket={socket} chatMessages={gameState.chatMessages} onTrack={trackAction} />
        )}

        {/* ── Gathering Node Overlay ── */}
        {gatherNode && !currentBattle && !currentConversation && !spottedMonster && (
          <div className="gather-overlay">
            <div className="gather-panel">
              <div className="gather-header">
                {gatherNode.type === 'mining' ? '⛏️ Mining Node' : '🌿 Herb Node'}
                <span className="gather-tier">({gatherNode.tier})</span>
              </div>
              {gatherResult ? (
                <div className="gather-result">
                  <div className="gather-result-title">Found:</div>
                  {gatherResult.materials.map((m, i) => (
                    <div key={i} className="gather-result-item">📦 {m.qty}x {m.name}</div>
                  ))}
                  <div className="gather-result-xp">+{gatherResult.xpGained} {gatherResult.discipline} XP</div>
                  <button className="gather-btn" onClick={handleGather} disabled={isGathering || gatherCooldown > 0}>
                    {isGathering ? '⏳ Gathering...' : gatherCooldown > 0 ? `⏳ Cooldown (${gatherCooldown}s)` : 'Gather Again'}
                  </button>
                </div>
              ) : (
                <div className="gather-prompt">
                  <div className="gather-prompt-text">
                    {gatherNode.type === 'mining' ? 'You see mineral deposits in the rock face.' : 'You spot useful herbs and plants growing here.'}
                  </div>
                  <button className="gather-btn" onClick={handleGather} disabled={isGathering || gatherCooldown > 0}>
                    {isGathering ? '⏳ Gathering...' : gatherCooldown > 0 ? `⏳ Cooldown (${gatherCooldown}s)` : gatherNode.type === 'mining' ? '⛏️ Mine' : '🌿 Gather'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Version Badge */}
        <div className="dm-version-badge">{buildVersion}</div>

        {showSettings && <SettingsPanel
          onClose={() => setShowSettings(false)}
          onShowPatchNotes={() => {
            setShowSettings(false);
            setPatchNotesSinceVersion(null);
            setShowPatchNotes(true);
          }}
        />}
        {showPatchNotes && !showWelcome && (
          <PatchNotesModal
            sinceVersion={patchNotesSinceVersion}
            onClose={() => setShowPatchNotes(false)}
          />
        )}
        {isAFK && (
          <AFKModal
            secondsLeft={afkSecondsLeft}
            onStay={dismissAFK}
            onLogout={() => { dismissAFK(); logout(); navigate('/login'); }}
          />
        )}
        {!isConnected && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10500,
              background: 'linear-gradient(180deg, #6a4a1a, #4a3010)',
              color: '#ffd96b',
              padding: '6px 16px',
              borderRadius: '0 0 8px 8px',
              border: '1px solid #c9a84c',
              borderTop: 'none',
              fontSize: 13,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}
            title="Trying to restore your connection…"
          >
            📡 Reconnecting…
          </div>
        )}
      </div>
      </CharacterProvider>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // CLASSIC DESKTOP GAME LAYOUT
  // ════════════════════════════════════════════════════════════════
  return (
    <CharacterProvider character={gameState.character}>
    <div className="game-container" data-mobile-tab={mobileTab}>
      {/* ── Welcome Modal (first-time) ── */}
      {showWelcome && <WelcomeModal onClose={handleWelcomeClose} characterId={gameState?.character?.id} />}
      {showPatchNotes && !showWelcome && (
        <PatchNotesModal
          sinceVersion={patchNotesSinceVersion}
          onClose={() => setShowPatchNotes(false)}
        />
      )}
      {/* ── v641.5: AFK auto-logout prompt ── */}
      {isAFK && (
        <AFKModal
          secondsLeft={afkSecondsLeft}
          onStay={dismissAFK}
          onLogout={() => { dismissAFK(); logout(); navigate('/login'); }}
        />
      )}
      {/* ── v641.5: Reconnect banner ── */}
      {!isConnected && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10500,
            background: 'linear-gradient(180deg, #6a4a1a, #4a3010)',
            color: '#ffd96b',
            padding: '6px 16px',
            borderRadius: '0 0 8px 8px',
            border: '1px solid #c9a84c',
            borderTop: 'none',
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          }}
          title="Trying to restore your connection…"
        >
          📡 Reconnecting…
        </div>
      )}
      {/* ── Contextual Hints ── */}
      <HintBanner character={gameState?.character} inCombat={!!currentBattle} hasQuest={gameState.questSummary?.active > 0} hasLevelUp={pendingLevelUp} onOpenGuide={openProgressionGuide} />
      {/* ── Quest Tracker ── */}
      {!currentBattle && (
        <QuestTracker
          questSummary={gameState.questSummary}
          onClick={() => setShowCompendium(true)}
        />
      )}
      {/* === Admin Broadcast Banner === */}
      {adminBroadcast && !broadcastDismissed && (
        <div className="broadcast-banner">
          <div className="broadcast-banner-content">
            <span className="broadcast-banner-icon">📢</span>
            <span className="broadcast-banner-message">{adminBroadcast.message}</span>
          </div>
          <button className="broadcast-banner-dismiss" onClick={() => setBroadcastDismissed(true)} title="Dismiss">✕</button>
        </div>
      )}
      {/* === Top Bar === */}
      <div className="top-bar">
        <h1>🏰 The Realms of Loria 🏰 {gameState.character?.current_area || 'Unknown Area'}</h1>
        <div className="top-bar-buttons">
          <MusicPlayer musicFile={gameState.maze?.music} paused={!!currentBattle || !!battleResult || !!gameState.fellowshipBattle} />
          <span className="connection-status" style={{ color: isConnected ? '#4ade80' : '#ef4444' }}>
            {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
          </span>
          <button className="btn nav-icon-btn" onClick={() => setShowInventory(true)} title="Inventory">
            <img src="/images/icons/nav-inventory.png" alt="Inventory" className="nav-icon" />
          </button>
          <button className="btn nav-icon-btn" onClick={() => setShowCharacterSheet(true)} title="Character">
            <img src="/images/icons/nav-character.png" alt="Character" className="nav-icon" />
          </button>
          <button className="btn nav-icon-btn" onClick={() => setShowSkillTree(true)} title="Skills">
            <img src="/images/icons/nav-skills.png" alt="Skills" className="nav-icon" />
          </button>
          <button className="btn nav-icon-btn" onClick={() => { setDeckHubInitTab('library'); setShowDeckHub(true); }} title="Deck" style={{ background: '#2a2a3a', color: '#ccc', fontSize: '1.1rem' }}>
            🃏
          </button>
          <button className="btn nav-icon-btn" onClick={() => setShowSpellbook(true)} title="Spellbook" style={{ background: '#2a2a3a', color: '#ccc', fontSize: '1.1rem' }}>
            📖
          </button>
          <button className="btn nav-icon-btn" onClick={() => setShowCompendium(true)} title="Compendium" style={{ background: '#2a3a2a', color: '#ccc', position: 'relative' }}>
            <img src="/images/icons/nav-compendium.png" alt="Compendium" className="nav-icon" />
            {gameState.questSummary && (gameState.questSummary.active > 0 || gameState.questSummary.available > 0) && (
              <span style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                minWidth: '18px',
                height: '18px',
                borderRadius: '9px',
                backgroundColor: gameState.questSummary.active > 0 ? '#f59e0b' : '#4caf50',
                color: '#000',
                fontSize: '11px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                boxShadow: '0 0 6px rgba(245, 158, 11, 0.6)',
                animation: 'quest-badge-pulse 2s ease-in-out infinite',
              }}>
                {gameState.questSummary.active > 0 ? gameState.questSummary.active : '!'}
              </span>
            )}
          </button>
          <button className="btn nav-icon-btn" onClick={async () => {
            if (generatingWorldMap) return;
            setGeneratingWorldMap(true);
            try {
              const token = localStorage.getItem('token');
              const resp = await fetch(`${API_BASE_URL}/api/game/worldmap?size=8`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (!resp.ok) throw new Error('Failed to generate map');
              const blob = await resp.blob();
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            } catch (err) {
              console.error('World map error:', err);
            } finally {
              setGeneratingWorldMap(false);
            }
          }} disabled={generatingWorldMap} style={{ background: '#2a4a2a', color: '#ccc' }} title="World Map">
            {generatingWorldMap ? '⏳' : <img src="/images/icons/nav-world-map.png" alt="World Map" className="nav-icon" />}
          </button>
          <span className="messages-btn-wrapper">
            <button className="btn nav-icon-btn" onClick={() => setShowMessages(true)} title="Messages">
              <img src="/images/icons/nav-messages.png" alt="Messages" className="nav-icon" />
            </button>
            {unreadMessageCount > 0 && (
              <span className="messages-badge">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>
            )}
          </span>
          <span className="messages-btn-wrapper">
            <button
              className="btn nav-icon-btn bounty-hud-btn"
              onClick={() => setShowActiveBounties(true)}
              title={bountyHudInfo?.claimable ? `${bountyHudInfo.claimable} bounties ready to claim!` : bountyHudInfo?.active ? `${bountyHudInfo.active} active bounties` : 'Active Bounties'}
              style={{ background: '#2a200a', color: '#f0c040', fontSize: '1.1rem' }}
            >
              📜
            </button>
            {bountyHudInfo?.claimable > 0 && (
              <span className="bounty-hud-badge bounty-hud-claimable">{bountyHudInfo.claimable}</span>
            )}
            {!bountyHudInfo?.claimable && bountyHudInfo?.active > 0 && (
              <span className="bounty-hud-badge bounty-hud-active">{bountyHudInfo.active}</span>
            )}
          </span>
          {user?.isAdmin && (
            <button className="admin-top-button" onClick={() => navigate('/admin')}>
              🛠️ Admin
            </button>
          )}
          <button className="btn" onClick={() => setShowBugReport(true)} style={{ background: '#4a2a2a', color: '#ccc' }} title="Report a Bug">
            🐛 Bug
          </button>
          <button className="btn" onClick={() => setShowCrownShop(true)} style={{ background: '#3a3000', color: '#ffd700' }} title="Crown Shop">
            👑 Shop
          </button>
          <button className="btn" onClick={() => setShowWorldTier(true)} style={{ background: '#1a2a3a', color: gameState.character?.world_tier > 0 ? {1:'#4fc3f7',2:'#66bb6a',3:'#ffa726',4:'#ab47bc',5:'#ef5350'}[gameState.character.world_tier] : '#aaa' }} title="World Tier">
            🌍 {gameState.character?.world_tier > 0 ? `T${gameState.character.world_tier}` : 'Tier'}
          </button>
          <button className="btn" onClick={() => setShowAtlas(true)} style={{ background: '#2a1a3a', color: '#c9a84c' }} title="Atlas">
            🧭 Atlas
          </button>
          <button className="btn" onClick={() => openProgressionGuide('overview')} style={{ background: '#1b2840', color: '#9fdcf7' }} title="Progression Guide">
            📘 Guide
          </button>
          <button className="btn" onClick={() => setShowSettings(true)} style={{ background: '#2a2a3a', color: '#ccc' }} title="Settings (audio, accessibility, font size)">
            ⚙️ Settings
          </button>
          <span className="online-counter-pill" title={`${gameState.onlinePlayers?.length || 0} adventurers online — click to view`} onClick={() => setShowDesktopOnline(p => !p)} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: '#1a3320', color: '#7fd494', borderRadius: '12px', fontSize: '0.85rem', cursor: 'pointer', border: '1px solid #2a5a3a' }}>
            👥 {gameState.onlinePlayers?.length || 0} online
          </span>
          <span style={{ position: 'relative' }} ref={switchPopupRef}>
            <button className="btn" onClick={(e) => { setShowSwitchPopup(p => !p); }} style={{ background: '#2a4a6a', color: '#ccc' }}>
              🔄 Switch
            </button>
            {showSwitchPopup && (() => {
              const rect = switchPopupRef.current?.getBoundingClientRect();
              const popupStyle = rect ? { top: rect.bottom + 4, right: window.innerWidth - rect.right } : {};
              return (
                <div className="switch-popup" style={popupStyle}>
                  <button className="switch-popup-item" onClick={() => { setShowSwitchPopup(false); handleSwitchCharacter(); }}>
                    <span className="switch-popup-item-icon">👤</span> Switch Character
                  </button>
                  <button className="switch-popup-item" onClick={() => { setShowSwitchPopup(false); const next = 'modern'; setDesktopLayout(next); localStorage.setItem('loria_desktop_layout', next); }}>
                    <span className="switch-popup-item-icon">🖥️</span> Modern UI
                  </button>
                </div>
              );
            })()}
          </span>
          <button className="btn btn-danger" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* === Mobile Navigation Tabs === */}
      <div className="mobile-nav">
        <button className={`mobile-nav-btn ${mobileTab === 'map' ? 'active' : ''}`} onClick={() => setMobileTab('map')}>
          🗺️ Map
        </button>
        <button className={`mobile-nav-btn ${mobileTab === 'stats' ? 'active' : ''}`} onClick={() => setMobileTab('stats')}>
          ⚔️ Stats
        </button>
        <button className={`mobile-nav-btn ${mobileTab === 'chat' ? 'active' : ''}`} onClick={() => setMobileTab('chat')}>
          💬 Chat
        </button>
      </div>

      {/* === Main Content === */}
      <div className="game-content">
        {/* Center: Online Players + Map + Game Log side by side, then Chat below */}
        <div className="map-area">
          <div className="map-and-log">
            <OnlinePlayersPanel
              onlinePlayers={gameState.onlinePlayers}
              character={gameState.character}
              onWhisper={(player) => setWhisperTarget(player)}
              onSendMail={(player) => {
                setMailRecipient(player.name);
                setShowMessages(true);
              }}
              onInspect={(id) => setInspectTargetId(id)}
              onFellowshipInvite={gameState.fellowship ? (targetId) => {
                if (socket) socket.emit('fellowship_invite', { targetId });
              } : null}
              onOpenGuildManage={() => setShowGuild(true)}
            />

            <MapGrid 
              character={gameState.character}
              maze={gameState.maze}
              nearbyPlayers={gameState.nearbyPlayers}
              onPlayerClick={(id) => setInspectTargetId(id)}
              onMove={(dir) => {
                if (!currentBattle && !gameState.fellowshipBattle && !overworldEvent) {
                  if (currentConversation) { setCurrentConversation(null); gameLogRef.current?.setPendingChoices(null); }
                  setSpottedMonster(null);
                  movePlayer(dir);
                }
              }}
            />

            {/* Treasure Map indicator — shown when the player has an active treasure map */}
            {gameState.character?.treasure_map && (() => {
              const tmRarity = gameState.character.treasure_map.rarity || 'common';
              const tmRarityLabel = { common: '', rare: 'Rare ', legendary: 'Legendary ' }[tmRarity] || '';
              return (
                <div className={`treasure-map-hud treasure-hud-${tmRarity}`}>
                  <span className="treasure-map-hud-icon">🗺️</span>
                  <span className="treasure-map-hud-text">
                    {tmRarityLabel && <strong>{tmRarityLabel}</strong>}
                    {gameState.character.treasure_map.area === gameState.character.current_area
                      ? 'Treasure nearby! Follow the map icon.'
                      : `Map leads to ${(gameState.character.treasure_map.area || '').replace(/\.dat$/, '').replace(/\d+$/, '')}`}
                  </span>
                  <button
                    className="treasure-map-hud-discard"
                    title="Discard this treasure map"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await fetch(`${API_BASE_URL}/api/treasure-map/discard`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` },
                        });
                        gameLogRef.current?.addLogEntry({
                          type: 'system',
                          text: '🗺️ Treasure map discarded.'
                        });
                        // Immediately clear the map from client state so HUD + map icon vanish
                        setGameState(prev => ({
                          ...prev,
                          character: prev.character ? { ...prev.character, treasure_map: null } : prev.character,
                          maze: prev.maze ? { ...prev.maze, treasureMapTile: null } : prev.maze,
                        }));
                        refreshCharacterData();
                      } catch (err) {
                        gameLogRef.current?.addLogEntry({ type: 'error', text: 'Failed to discard map.' });
                      }
                    }}
                  >
                    ✕
                  </button>
                </div>
              );
            })()}

            {/* Mobile-only monster encounter bar (visible on map tab so you don't have to switch to Stats) */}

            {/* Campfire XP Buff HUD indicator */}
            {(() => {
              let activeBuffs = gameState.character?.active_buffs;
              try { activeBuffs = typeof activeBuffs === 'string' ? JSON.parse(activeBuffs) : (activeBuffs || []); } catch(e) { activeBuffs = []; }
              const cfBuff = activeBuffs.find(b => b.type === 'campfire_xp' && b.expiresAt && Date.now() < b.expiresAt);
              if (!cfBuff) return null;
              const remaining = cfBuff.expiresAt - Date.now();
              const mins = Math.ceil(remaining / 60000);
              return (
                <div className="campfire-buff-hud">
                  <span className="campfire-buff-icon">🔥</span>
                  <span className="campfire-buff-text">+{cfBuff.value || 10}% XP ({mins}m)</span>
                </div>
              );
            })()}

            {spottedMonster && (
              <div className="mobile-encounter-bar">
                <img
                  src={`/images/monsters/${spottedMonster.monster.pic}`}
                  alt={spottedMonster.monster.name}
                  onError={(e) => { e.target.src = '/images/monsters/default.jpg'; }}
                  className="mobile-encounter-img"
                />
                <div className="mobile-encounter-info">
                  <div className="mobile-encounter-name">⚔️ {spottedMonster.monster.name}</div>
                  <div className="mobile-encounter-level">Level {spottedMonster.monster.level}</div>
                </div>
                <button
                  className="mobile-encounter-btn"
                  onClick={() => handleAttackMonster(spottedMonster.monster.name)}
                  disabled={attackPending}
                >
                  {attackPending ? '⏳ Starting...' : '⚔️ Fight'}
                </button>
              </div>
            )}

            <GameLog 
              ref={gameLogRef}
              character={gameState.character}
              onConversationChoice={handleConversationChoice}
              onConversationClose={closeConversation}
            />

            {/* Campfire Rest Result — shown after campfire event resolves */}
            {campfireRestResult && !overworldEvent && (
              <div className="campfire-overlay">
                <div className="campfire-panel campfire-result">
                  <div className="campfire-header">🔥 Campfire Rest</div>
                  <div className="campfire-reward-list">
                    <div className="campfire-reward-item campfire-reward-heal">❤️ Full HP Restored</div>
                    <div className="campfire-reward-item campfire-reward-heal">💧 Full MP Restored</div>
                    <div className="campfire-reward-item campfire-reward-xp">✨ +10% XP Bonus for 1 Hour</div>
                  </div>
                  <button
                    className="campfire-rest-btn"
                    onClick={() => setCampfireRestResult(false)}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Campfire Rest Button — shown when adjacent to a campfire tile */}
            {isNearCampfire && !campfireRestResult && !currentBattle && !currentConversation && !spottedMonster && !overworldEvent && (
              <div className="campfire-overlay">
                <div className="campfire-panel">
                  <div className="campfire-header">🔥 Campfire</div>
                  <div className="campfire-prompt">
                    A warm campfire crackles nearby. Rest to restore your health and gain an experience bonus.
                  </div>
                  <button
                    className="campfire-rest-btn"
                    onClick={() => {
                      if (socket) {
                        socket.emit('rest_at_campfire');
                        gameLogRef.current?.addLogEntry({
                          type: 'system',
                          text: '🔥 You settle in by the campfire...'
                        });
                      }
                    }}
                  >
                    🔥 Rest at Campfire
                  </button>
                </div>
              </div>
            )}
          </div>

          <ChatPanel
            chatMessages={gameState.chatMessages}
            onSendMessage={sendChatMessage}
            character={gameState.character}
            nearbyPlayers={gameState.nearbyPlayers}
            chatType={chatType}
            onChatTypeChange={setChatType}
            whisperTarget={whisperTarget}
            onClearWhisperTarget={() => setWhisperTarget(null)}
            fellowship={gameState.fellowship}
            socket={socket}
            onWhisper={(player) => setWhisperTarget(player)}
            onInspect={(id) => setInspectTargetId(id)}
            onReport={(target, recentMessages) => setReportTarget({ target, recentMessages })}
            onOpenCoC={() => setShowCoC(true)}
          />
        </div>

        {/* Right: Side Panel */}
        <div className="side-panel">
          <CharacterStats character={gameState.character} crownPulse={crownPulse} />
          
          <ActionsPanel 
            character={gameState.character}
            nearbyPlayers={gameState.nearbyPlayers}
            spottedMonster={spottedMonster}
            attackPending={attackPending}
            onAttackMonster={handleAttackMonster}
            onUseItem={handleUseItem}
            onUseItemOnPlayer={handleUseItemOnPlayer}
            onCastSpell={handleCastSpell}
            onCastSpellOnPlayer={handleCastSpellOnPlayer}
            onCastSpellAtMonster={handleCastSpellAtMonster}
            onLogMessage={handleLogMessage}
            onDismissPet={handleDismissPet}
          />
        </div>
      </div>

      {/* === Bottom Hotkey Bar === */}
      <div className="bottom-bar">
        <span className="hotkey"><kbd>WASD</kbd> Move</span>
        <span className="hotkey"><kbd>I</kbd> Inventory</span>
        <span className="hotkey"><kbd>C</kbd> Character</span>
        <span className="hotkey"><kbd>T</kbd> Skills</span>
        <span className="hotkey"><kbd>J</kbd> Compendium</span>
        <span className="hotkey"><kbd>B</kbd> Shop</span>
        <span className="hotkey"><kbd>M</kbd> Market</span>
        <span className="hotkey"><kbd>N</kbd> Arena</span>
        <span className="hotkey clickable" onClick={() => setShowGuild(true)}>⚔️ Guild</span>
        <span className="hotkey clickable" onClick={() => setShowFellowship(true)}>🤝 Fellowship</span>
        <span className="hotkey"><kbd>Esc</kbd> Close</span>
      </div>

      {/* === Modals === */}
      {/* v642.1: Character Hub tab strip */}
      <CharacterHubTabBar activeTab={characterHubTab} onSwitch={switchCharacterHubTab} />
      {/* v642.2: Adventure Hub tab strip */}
      <AdventureHubTabBar activeTab={adventureHubTab} onSwitch={switchAdventureHubTab} />
      {showCharacterSheet && (
        <CharacterSheet
          character={gameState.character}
          onClose={() => setShowCharacterSheet(false)}
          onCharacterUpdate={refreshCharacterData}
          onTeleport={handleTeleport}
        />
      )}

      {showInventory && (
        <InventoryPanel
          character={gameState.character}
          onClose={() => setShowInventory(false)}
          onCharacterUpdate={refreshCharacterData}
          onLogMessage={(msg) => gameLogRef.current?.addLogEntry({ type: 'action', text: msg })}
          onTeleport={handleTeleport}
        />
      )}

      {currentBattle && (
        <CombatInterface
          battle={currentBattle}
          onAction={handleCombatAction}
          onClose={handleCloseCombat}
          battleMusic={battleMusic}
          onTrack={trackAction}
          onCombatError={combatErrorRef.current}
          characterPortrait={CHARACTER_PORTRAITS[gameState.character?.name]}
          characterId={gameState.character?.id}
        />
      )}

      {battleResult && (
        <PostBattleScreen
          battleResult={battleResult}
          character={gameState.character}
          questSummary={gameState.questSummary}
          onClose={handleClosePostBattle}
          onCharacterUpdate={refreshCharacterData}
          onOpenSpellbook={() => setShowSpellbook(true)}
        />
      )}

      {pendingLevelUp && !battleResult && !currentBattle && (
        <LevelUpModal
          character={gameState.character}
          onCharacterUpdate={refreshCharacterData}
          onClose={() => setPendingLevelUp(false)}
        />
      )}

      {pendingACSelection && !pendingLevelUp && !battleResult && !currentBattle && (
        <AdvancedClassSelection
          character={gameState.character}
          onCharacterUpdate={refreshCharacterData}
          onClose={() => setPendingACSelection(false)}
        />
      )}

      {inventoryOverflow && !battleResult && (
        <InventoryOverflowModal
          inventory={inventoryOverflow.inventory}
          maxCapacity={inventoryOverflow.maxCapacity}
          onResolved={() => {
            // Don't clear inventoryOverflow here — the useEffect above will close
            // the modal once refreshCharacterData() returns a clean inventory
            // (no _new flags, within capacity).  Clearing it here first caused a
            // race where the still-stale gameState would immediately re-open the
            // modal on the very next render tick.
            refreshCharacterData();
          }}
        />
      )}

      {/* Warp choice modal */}
      {warpChoice && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: 'var(--bg-panel, #2a2a2a)', border: '2px solid var(--border-gold, #b89d65)',
            borderRadius: '8px', padding: '1.5rem', maxWidth: '360px', width: '90%',
            textAlign: 'center', color: 'var(--text-primary, #ddd)',
            fontFamily: 'var(--font-ui, inherit)'
          }}>
            {warpChoice.warpType === 'waypoints' ? (<>
              <div style={{ fontSize: '1.1rem', marginBottom: '0.8rem', color: 'var(--text-gold, #d4af37)' }}>
                🌐 Warp III — Waypoint Network
              </div>
              <div style={{ fontSize: '0.8rem', marginBottom: '0.8rem', color: '#aaa' }}>
                {warpChoice.waypoints.length}/{warpChoice.maxWaypoints} waypoints set
              </div>
              {warpChoice.waypoints.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.8rem' }}>
                  {warpChoice.waypoints.map((wp, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                      <button
                        onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); handleCastSpell(spell, { action: 'warp_to_waypoint', slot: wp.slot }); }}
                        style={{
                          flex: 1, padding: '0.4rem 0.6rem', fontSize: '0.8rem', cursor: 'pointer',
                          background: '#2a5a3a', border: '1px solid #4a8a5a',
                          borderRadius: '4px', color: '#aaffaa', fontFamily: 'var(--font-ui, inherit)',
                          textAlign: 'left', transition: 'background 0.15s'
                        }}
                        onMouseEnter={e => e.target.style.background = '#3a6a4a'}
                        onMouseLeave={e => e.target.style.background = '#2a5a3a'}
                      >
                        🌀 {wp.name} <span style={{ color: '#8dc6ff', fontSize: '0.7rem' }}>({wp.label.split('(').slice(1).join('(')}</span>
                      </button>
                      <button
                        onClick={() => { const spell = warpChoice.spellName; setWarpChoice(null); setWaypointName(''); handleCastSpell(spell, { action: 'set_waypoint', slot: wp.slot, waypointName: waypointName || undefined }); }}
                        title="Overwrite with current location"
                        style={{
                          padding: '0.4rem', fontSize: '0.7rem', cursor: 'pointer',
                          background: '#444', border: '1px solid #666', borderRadius: '4px', color: '#ddd',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={e => e.target.style.background = '#555'}
                        onMouseLeave={e => e.target.style.background = '#444'}
                      >📌</button>
                    </div>
                  ))}
                </div>
              )}
              {warpChoice.waypoints.length < warpChoice.maxWaypoints && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
                  <input
                    type="text" placeholder="Waypoint name (optional)" maxLength={24}
                    value={waypointName} onChange={e => setWaypointName(e.target.value)}
                    style={{
                      padding: '0.4rem 0.6rem', fontSize: '0.8rem', background: '#1a1a1a',
                      border: '1px solid #555', borderRadius: '4px', color: '#ddd',
                      fontFamily: 'var(--font-ui, inherit)', textAlign: 'center'
                    }}
                  />
                  <button
                    onClick={() => { const spell = warpChoice.spellName; const name = waypointName; setWarpChoice(null); setWaypointName(''); handleCastSpell(spell, { action: 'set_waypoint', waypointName: name || undefined }); }}
                    style={{
                      padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer',
                      background: 'var(--bg-panel-alt, #444)', border: '1px solid var(--border-dark, #555)',
                      borderRadius: '4px', color: 'var(--text-primary, #ddd)',
                      fontFamily: 'var(--font-ui, inherit)', transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => e.target.style.background = '#555'}
                    onMouseLeave={e => e.target.style.background = 'var(--bg-panel-alt, #444)'}
                  >
                    📌 Save current location as waypoint
                  </button>
                </div>
              )}
              <button
                onClick={() => { setWarpChoice(null); setWaypointName(''); }}
                style={{
                  padding: '0.35rem 1rem', fontSize: '0.78rem', cursor: 'pointer',
                  background: 'transparent', border: '1px solid #555',
                  borderRadius: '4px', color: '#999',
                  fontFamily: 'var(--font-ui, inherit)', marginTop: '0.3rem'
                }}
              >
                Cancel
              </button>
            </>) : (<>
              <div style={{ fontSize: '1.1rem', marginBottom: '0.8rem', color: 'var(--text-gold, #d4af37)' }}>
                ✨ Warp II
              </div>
              <div style={{ fontSize: '0.85rem', marginBottom: '1rem', lineHeight: '1.4' }}>
                {warpChoice.hasExistingMark
                  ? <>Your mark is set at <strong style={{ color: '#8dc6ff' }}>{warpChoice.markLabel}</strong>.</>
                  : <>You have no marked location yet.</>
                }
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    const spell = warpChoice.spellName;
                    setWarpChoice(null);
                    handleCastSpell(spell, { action: 'set_mark' });
                  }}
                  style={{
                    padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer',
                    background: 'var(--bg-panel-alt, #444)', border: '1px solid var(--border-dark, #555)',
                    borderRadius: '4px', color: 'var(--text-primary, #ddd)',
                    fontFamily: 'var(--font-ui, inherit)', transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.target.style.background = '#555'}
                  onMouseLeave={e => e.target.style.background = 'var(--bg-panel-alt, #444)'}
                >
                  📌 Set current location as mark
                </button>
                <button
                  onClick={() => {
                    const spell = warpChoice.spellName;
                    setWarpChoice(null);
                    handleCastSpell(spell, { action: 'warp_to_mark' });
                  }}
                  disabled={!warpChoice.hasExistingMark}
                  style={{
                    padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: warpChoice.hasExistingMark ? 'pointer' : 'not-allowed',
                    background: warpChoice.hasExistingMark ? '#2a5a3a' : '#333',
                    border: '1px solid ' + (warpChoice.hasExistingMark ? '#4a8a5a' : '#444'),
                    borderRadius: '4px', color: warpChoice.hasExistingMark ? '#aaffaa' : '#666',
                    fontFamily: 'var(--font-ui, inherit)', transition: 'background 0.15s',
                    opacity: warpChoice.hasExistingMark ? 1 : 0.5
                  }}
                  onMouseEnter={e => { if (warpChoice.hasExistingMark) e.target.style.background = '#3a6a4a'; }}
                  onMouseLeave={e => { if (warpChoice.hasExistingMark) e.target.style.background = '#2a5a3a'; }}
                >
                  🌀 Warp to mark{warpChoice.hasExistingMark ? ` (${warpChoice.markLabel})` : ''}
                </button>
                <button
                  onClick={() => setWarpChoice(null)}
                  style={{
                    padding: '0.35rem 1rem', fontSize: '0.78rem', cursor: 'pointer',
                    background: 'transparent', border: '1px solid #555',
                    borderRadius: '4px', color: '#999',
                    fontFamily: 'var(--font-ui, inherit)', marginTop: '0.3rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </>)}
          </div>
        </div>
      )}

      {showShop && (
        <ShopModal
          character={gameState.character}
          onClose={() => setShowShop(false)}
          onCharacterUpdate={refreshCharacterData}
        />
      )}

      {showCrownShop && (
        <CrownShopModal
          character={gameState.character}
          onClose={() => setShowCrownShop(false)}
          onCharacterUpdate={refreshCharacterData}
        />
      )}

      {showInfiniteQuest && (
        <InfiniteQuestPanel
          character={gameState.character}
          onClose={() => setShowInfiniteQuest(false)}
          onCharacterUpdate={refreshCharacterData}
          onOpenCrownShop={() => setShowCrownShop(true)}
        />
      )}

      {showWorldTier && (
        <WorldTierModal
          character={gameState.character}
          onClose={() => setShowWorldTier(false)}
          onTierChanged={() => refreshCharacterData()}
          onOpenGuide={openProgressionGuide}
        />
      )}

      {showAtlas && (
        <AtlasPanel
          character={gameState.character}
          onClose={() => setShowAtlas(false)}
          onOpenGuide={openProgressionGuide}
        />
      )}

      {showProgressionGuide && (
        <ProgressionGuidePanel
          onClose={() => setShowProgressionGuide(false)}
          initialSection={guideSection}
        />
      )}

      {showMessages && (
        <MessagesModal
          character={gameState.character}
          onClose={() => { setShowMessages(false); setMailRecipient(null); }}
          onUnreadUpdate={fetchUnreadCount}
          onCharacterUpdate={refreshCharacterData}
          initialComposeTo={mailRecipient}
        />
      )}

      {showMarket && (
        <MarketModal
          character={gameState.character}
          onClose={() => setShowMarket(false)}
          onCharacterUpdate={refreshCharacterData}
        />
      )}

      {showArena && (
        <ArenaModal
          character={gameState.character}
          socket={socket}
          onClose={() => { if (!inArenaBattle) setShowArena(false); }}
        />
      )}

      {showSkillTree && (
        <SkillTreePanel
          character={gameState.character}
          onClose={() => setShowSkillTree(false)}
          onCharacterUpdate={refreshCharacterData}
        />
      )}

      {showCrafting && (
        <CraftingPanel
          character={gameState.character}
          onClose={() => setShowCrafting(false)}
          onCharacterUpdate={refreshCharacterData}
        />
      )}

      {showStash && (
        <StashPanel
          character={gameState.character}
          onClose={() => setShowStash(false)}
          onCharacterUpdate={refreshCharacterData}
        />
      )}

      {showDeckHub && (
        <DeckManager
          character={gameState.character}
          initialTab={deckHubInitTab}
          onClose={() => setShowDeckHub(false)}
        />
      )}
      {showSpellbook && <SpellbookPanel onClose={() => setShowSpellbook(false)} />}
      {showCoC && <CodeOfConductModal onClose={() => setShowCoC(false)} />}

      {showBountyBoard && (
        <BountyBoardPanel
          character={gameState.character}
          area={gameState.character?.current_area}
          onClose={() => setShowBountyBoard(false)}
          onCharacterUpdate={refreshCharacterData}
          onOpenGuide={openProgressionGuide}
        />
      )}

      {showDelve && (
        <DelveModal
          character={gameState.character}
          socket={socket}
          currentBattle={currentBattle}
          onBattleStart={() => setShowDelve(false)}
          onClose={() => setShowDelve(false)}
          onCharacterUpdate={refreshCharacterData}
          onOpenGuide={openProgressionGuide}
        />
      )}

      {overworldEvent && (
        <OverworldEventModal
          event={overworldEvent}
          result={overworldEventResult}
          nextStep={overworldNextStep}
          character={gameState.character}
          onChoose={async (eventId, optionId) => {
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${API_BASE_URL}/api/overworld-events/resolve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ eventId, optionId }),
              });
              const data = await res.json();
              if (data.error) {
                gameLogRef.current?.addLogEntry({ type: 'error', text: data.error });
                return;
              }
              setOverworldEventResult(data.resolution);
              setOverworldNextStep(data.nextStep || null);
              // Campfire rest bonuses: log the heal + XP buff and show result modal
              if (data.campfireRest) {
                setCampfireRestResult(true);
                gameLogRef.current?.addLogEntry({
                  type: 'system',
                  text: '🔥 The campfire\'s warmth restores you to full health and mana.'
                });
                gameLogRef.current?.addLogEntry({
                  type: 'system',
                  text: '✨ You feel invigorated — +10% XP bonus for the next hour!'
                });
              }
              refreshCharacterData();
            } catch (err) {
              gameLogRef.current?.addLogEntry({ type: 'error', text: 'Failed to resolve event.' });
            }
          }}
          onContinue={() => {
            // Advance to the next step in a multi-step treasure event
            if (overworldNextStep) {
              setOverworldEvent(prev => ({
                ...prev,
                description: overworldNextStep.description,
                options: overworldNextStep.options,
                currentStep: overworldNextStep.currentStep,
              }));
              setOverworldEventResult(null);
              setOverworldNextStep(null);
            }
          }}
          onDismiss={() => {
            setOverworldEvent(null);
            setOverworldEventResult(null);
            setOverworldNextStep(null);
            refreshCharacterData();
          }}
        />
      )}

      {showActiveBounties && (
        <ActiveBountiesPanel
          onClose={() => setShowActiveBounties(false)}
          onOpenGuide={openProgressionGuide}
        />
      )}

      {showBugReport && (
        <BugReportModal
          gameState={gameState}
          recentActions={recentActionsRef.current}
          currentBattle={currentBattle}
          onClose={() => setShowBugReport(false)}
        />
      )}

      {showCompendium && (
        <Compendium
          character={gameState.character}
          onClose={() => setShowCompendium(false)}
          onOpenGuide={openProgressionGuide}
          initialQuestFilter={
            gameState?.questSummary?.active === 0 && gameState?.questSummary?.available > 0
              ? 'available'
              : 'all'
          }
          onOpenWorldMap={async () => {
            if (generatingWorldMap) return;
            setGeneratingWorldMap(true);
            try {
              const token = localStorage.getItem('token');
              const resp = await fetch(`${API_BASE_URL}/api/game/worldmap?size=8`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (!resp.ok) throw new Error('Failed to generate map');
              const blob = await resp.blob();
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            } catch (err) {
              console.error('World map error:', err);
            } finally {
              setGeneratingWorldMap(false);
            }
          }}
        />
      )}

      {showGuild && (
        <GuildPanel
          character={gameState.character}
          onClose={() => setShowGuild(false)}
          onCharacterUpdate={refreshCharacterData}
        />
      )}

      {inspectTargetId && (
        <InspectModal
          targetId={inspectTargetId}
          onClose={() => setInspectTargetId(null)}
        />
      )}



      {showFellowship && (
        <FellowshipPanel
          fellowship={gameState.fellowship}
          character={gameState.character}
          onlinePlayers={gameState.onlinePlayers}
          socket={socket}
          onInspect={(id) => setInspectTargetId(id)}
          onClose={() => setShowFellowship(false)}
        />
      )}

      {/* Fellowship invite notification */}
      {gameState.fellowshipInvite && (
        <div className="fellowship-invite-popup">
          <div className="fellowship-invite-content">
            <p>🤝 <strong>{gameState.fellowshipInvite.leaderName}</strong> invites you to join their fellowship!</p>
            <div className="fellowship-invite-btns">
              <button className="btn-primary" onClick={() => {
                if (socket) socket.emit('fellowship_accept', { fellowshipId: gameState.fellowshipInvite.fellowshipId });
                dismissFellowshipInvite();
              }}>Accept</button>
              <button className="btn-danger" onClick={() => {
                if (socket) socket.emit('fellowship_decline', { fellowshipId: gameState.fellowshipInvite.fellowshipId });
                dismissFellowshipInvite();
              }}>Decline</button>
            </div>
          </div>
        </div>
      )}

      {/* Fellowship Combat Interface — hidden when PostBattleScreen is showing (battleResult set) */}
      {gameState.fellowshipBattle && !battleResult && (
        <FellowshipCombatInterface
          battleState={gameState.fellowshipBattle}
          character={gameState.character}
          socket={socket}
          chatMessages={gameState.chatMessages}
          onTrack={trackAction}
        />
      )}



      {/* Gathering Node Overlay */}
      {gatherNode && !currentBattle && !currentConversation && !spottedMonster && (
        <div className="gather-overlay">
          <div className="gather-panel">
            <div className="gather-header">
              {gatherNode.type === 'mining' ? '⛏️ Mining Node' : '🌿 Herb Node'}
              <span className="gather-tier">({gatherNode.tier})</span>
            </div>
            {gatherResult ? (
              <div className="gather-result">
                <div className="gather-result-title">Found:</div>
                {gatherResult.materials.map((m, i) => (
                  <div key={i} className="gather-result-item">📦 {m.qty}x {m.name}</div>
                ))}
                <div className="gather-result-xp">+{gatherResult.xpGained} {gatherResult.discipline} XP</div>
                <button className="gather-btn" onClick={handleGather} disabled={isGathering || gatherCooldown > 0}>
                  {isGathering ? '⏳ Gathering...' : gatherCooldown > 0 ? `⏳ Cooldown (${gatherCooldown}s)` : 'Gather Again'}
                </button>
              </div>
            ) : (
              <div className="gather-prompt">
                <div className="gather-prompt-text">
                  {gatherNode.type === 'mining'
                    ? 'You see mineral deposits in the rock face.'
                    : 'You spot useful herbs and plants growing here.'}
                </div>
                <button className="gather-btn" onClick={handleGather} disabled={isGathering || gatherCooldown > 0}>
                  {isGathering ? '⏳ Gathering...' : gatherCooldown > 0 ? `⏳ Cooldown (${gatherCooldown}s)` : gatherNode.type === 'mining' ? '⛏️ Mine' : '🌿 Gather'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Runtime build version badge — reflects the live Heroku release */}
      <div style={{
        position: 'fixed',
        bottom: 'max(8px, env(safe-area-inset-bottom))',
        right: 'max(12px, env(safe-area-inset-right))',
        padding: '0.18rem 0.42rem',
        borderRadius: '999px',
        fontSize: '0.68rem',
        color: 'rgba(255,255,255,0.72)',
        background: 'rgba(0,0,0,0.42)',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 1px 6px rgba(0,0,0,0.28)',
        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 5000,
        letterSpacing: '0.04em',
      }}>
        {buildVersion}
      </div>

      {showSettings && <SettingsPanel
        onClose={() => setShowSettings(false)}
        onShowPatchNotes={() => {
          setShowSettings(false);
          setPatchNotesSinceVersion(null); // show all entries
          setShowPatchNotes(true);
        }}
      />}
    </div>
    </CharacterProvider>
  );
};

export default GamePage;
